import { Injectable } from '@angular/core';
import { INavInfo } from '../../model/models/layout/nav-info.interface';
import { MissingTransactionInfoType } from '../../model/types/missing-transaction-info-type';
import { TranslateService } from '@ngx-translate/core';
import { APP_CONFIG, Region } from '../../environment';
import { ColorProvider } from '../../providers/color/color';
import { AnalyticsEventType, ContactModalReasons, SegmentLocationNames } from '../../model/types/analytics.const';
import { DocType } from '../../model/types/doc-type';
import { KYC_METHODS } from '../myria/app-variables';
import { EkycFlowState } from '../myria/ekyc-flow-state';

/*
  Directory of all the page navigation items
*/
@Injectable()
export class PageDirectoryProvider {

  constructor(private translate: TranslateService) { }

  getNavInfo(pageName, params = {}, isRootPage = false, icon = null, title = null, id='') {
    return {
      page: pageName,
      isRootPage: isRootPage,
      icon: icon,
      title: title,
      params: params,
      id:id
    };
}

  get FirstPage(): INavInfo {
    return this.getNavInfo('__firstPage'); // not a real page - just a navigation hint
  }

  get LoginPage(): INavInfo {
    return this.getNavInfo('LoginPage');
  }

  get PreLoginPage(): INavInfo {
    return this.getNavInfo('PreLoginStoriesPage', {
      redirected: true
    }, true);
  }

  get SigninPage(): INavInfo {
    return this.getNavInfo('SigninPage');
  }

  get DebugMenuPage(): INavInfo {
    return this.getNavInfo('DebugMenuPage');
  }

  get SigninFailedPage(): INavInfo {
    return this.getNavInfo('SigninFailedPage', {
      error: null
    });
  }

  get SignInForgotResetPassPage(): INavInfo {
    return this.getNavInfo('SignInForgotResetPassPage');
  }

  get GroupPartnersListPage(): INavInfo {
    return this.getNavInfo('GroupPartnersListPage');
  }

  get TabsHomePage(): INavInfo {
      return this.getNavInfo('TabsHomePage');
  }

  get TabsHomeActivityPage(): INavInfo {
    return this.getNavInfo('TabsHomePage', {
      tab: this.ActivityPage
    });
  }

  get TabsHomeRecipientListPage(): INavInfo {
    return this.getNavInfo('TabsHomePage', {
      tab: this.RecipientListPage
    });
  }

  get TabsHomeFindLocationPage(): INavInfo {
    return this.getNavInfo('TabsHomePage', {
      tab: this.StoresPage
    });
  }

  get TabsHomeTrackTransferPage(): INavInfo {
    return this.getNavInfo('TabsHomePage', {
      tab: this.TrackTransferPage
    });
  }

  get TabsHomeWalletPage(): INavInfo {
    return this.getNavInfo('TabsHomePage', {
      tab: this.WalletHome
    });
  }

  get TabsHomeHubPage(): INavInfo {
    return this.getNavInfo('TabsHomePage', {
      tab: this.HubPage
    });
  }

  get MarketingPage(): INavInfo {
    return this.getNavInfo('MarketingPage', {
      marketing: {}
    });
  }

  get CheckPricesPage(): INavInfo {
    return this.getNavInfo('CheckPricesPage', {
      sendFromRecipient: false
    });
  }

  get ActivityFilterPage(): INavInfo {
    return this.getNavInfo('ActivityFilterPage');
  }

  get ActivityAllPage(): INavInfo {
    return this.getNavInfo('ActivityAllPage');
  }

  get WalletKYCPromptPage(): INavInfo {
    return this.getNavInfo('WalletKYCPromptPage');
  }

  get ConfirmEmailPage(): INavInfo {
    // return this.getNavInfo('ConfirmEmailPage');
    return this.getNavInfo('ModalAlertPage', {
      showRightClose: true,
      goToActivity: true,
      img: 'assets/imgs/Identity.svg',
      title: this.translate.instant('page.settingsPersonalPage.confirmEmailChangeHeader'),
      msg: this.translate.instant('page.settingsPersonalPage.confirmEmailChangeContent'),
      primaryButtonText: this.translate.instant('global.gotItButton'),
      showFullPage: true,
  });
  }

  get PermissionTrackingPage(): INavInfo {
    return this.getNavInfo('PermissionTrackingPage');
  }

  get ForeignIpGeolocationPage(): INavInfo {
    return this.getNavInfo('ForeignIpGeolocationPage');
  }

  get UserCountryPage(): INavInfo {
    return this.getNavInfo('UserCountryPage');
  }

  get RlUserCountryPage(): INavInfo {
    return this.getNavInfo('RlUserCountryPage');
  }

  get UserCountrySelectPage(): INavInfo {
    return this.getNavInfo('UserCountrySelectPage', {
      countries: null,
      onSelect: null
    });
  }

  get UserCountryNotSupportedPage(): INavInfo {
    return this.getNavInfo('UserCountryNotSupportedPage');
  }

  get CountryDeclarationPage(): INavInfo {
    return this.getNavInfo('CountryDeclarationPage');
  }

  get LocationListPage(): INavInfo {
    return this.getNavInfo('LocationListPage');
  }

  get WalletNotActivePage(): INavInfo {
    return this.getNavInfo('WalletNotActivePage', {}, false, 'custom-icon-tab-wallet', 'global.eWalletNav','walletTabBtn');
  }

  get WalletWaitlistPage(): INavInfo {
    return this.getNavInfo('WalletWaitlistPage');
  }

  get WalletHome(): INavInfo {
    return this.getNavInfo('WalletHomePage', {}, false, 'custom-icon-tab-wallet', 'global.eWalletNav','walletTabBtn');
  }

  get WalletOnboardingTab(): INavInfo {
    return this.getNavInfo('WalletNotActivePage', {}, false, 'custom-icon-tab-credit-card', 'global.cardsNav', 'walletOnboardingTabBtn');
  }

  get ActivityPage(): INavInfo {
    if(APP_CONFIG.region.region === Region.MY){
      return this.getNavInfo('ActivityPage', {}, false, 'custom-icon-tab-activity', 'page.activityPage.title','activityTabBtn');
    } else {
      return this.getNavInfo('ActivityPage', {}, false, 'custom-icon-tab-home', 'page.activityPage.titleHome','activityTabBtn');
    }
  }

  get ActivityRemittancePage(): INavInfo {
    return this.getNavInfo('ActivityRemittancePage', {}, false, 'custom-icon-tab-activity', 'page.activityPage.title');
  }

  get RecipientListPage(): INavInfo {
    return this.getNavInfo('RecipientListPage', {}, false, 'custom-icon-tab-recipients', 'page.myRecipientsPage.recipientsHeader','recipientListTabBtn');
  }

  get StatementsAndReportsPage(): INavInfo {
    return this.getNavInfo('StatementsReportsPage');
  }

  get TabMorePage(): INavInfo {
    return this.getNavInfo('TabMorePage', {}, false, 'custom-icon-tab-more', 'global.moreNav', 'moreTabBtn');
  }
  get TabMoreNonActivatedWalletPage(): INavInfo {
    return this.getNavInfo('TabMorePage', {}, false, 'custom-icon-tab-more', 'global.moreNav', 'moreTabBtn');
  }

  get TabHubPage(): INavInfo {
    return this.getNavInfo('HubPage', {}, false, 'custom-icon-tab-hub', 'global.mainNavigation.hubNav', 'hubTabBtn');
  }

  get TabSendMorePage(): INavInfo {
    return this.getNavInfo('TabSendMorePage', {}, false, 'custom-icon-send', 'global.moreNav','moreTabBtn');
  }

  get SendPageAlt(): INavInfo {
    return this.getNavInfo('CheckPricesPage', {}, false, 'custom-icon-send', 'global.mainNavigation.sendMoneyNavigation', 'sendMoneyTabBtnAlt');
  }
  get ChoosePaymentMethod(): INavInfo {
    return {
      page: 'PaymentMethodPage',
      icon: 'custom-icon-tab-send',
      title: 'global.mainNavigation.sendMoneyNavigation',
    }
  }

  get RedesignNewOrderSelectCountryPage(): INavInfo {
    return {
      page: "RedesignNewOrderSelectCountryPage",
    }
  }

  get RecipientDetailsPage(): INavInfo {
    return this.getNavInfo('RecipientDetailsPage', {
      recipientId: null
    });
  }

  get RecipientModifyPage(): INavInfo {
    return this.getNavInfo('RecipientModifyPage');
  }

  get RecipientOrderModifyPage(): INavInfo {
    return this.getNavInfo('RecipientOrderModifyPage');
  }

  get RecipientAddPage(): INavInfo {
    return this.getNavInfo('RecipientDetailsPage');
  }

  get PaymentAccountListPage(): INavInfo {
    return this.getNavInfo('PaymentAccountListPage');
  }

  get PaymentAccountAddPage(): INavInfo {
    return this.getNavInfo('PaymentAccountDetailsPage');
  }

  get SendPayPoliPage(): INavInfo {
    return this.getNavInfo('SendPayPoliPage');
  }

  get SendPayKlarnaPage(): INavInfo {
    return this.getNavInfo('SendPayKlarnaPage');
  }

  get BankDepositOptionalBeneficiaryPage(): INavInfo {
    return this.getNavInfo('BankDepositOptionalBeneficiaryPage');
  }

  get SendPayFpxPage(): INavInfo {
    return this.getNavInfo('SendPayFpxPage');
  }

  get SendPayIDealPage(): INavInfo {
    return this.getNavInfo('SendPayIDealPage')
  }

  get SendPayBancontactPage(): INavInfo {
    return this.getNavInfo('SendPayBancontactPage')
  }

  get SendPayGiropayPage(): INavInfo {
    return this.getNavInfo('SendPayGiropayPage')
  }

  get KlarnaPrivacyPage(): INavInfo {
    return this.getNavInfo('KlarnaPrivacyPage');
  }

  get SendPaymentPendingSencillito(): INavInfo {
    return this.getNavInfo('SendPaymentpendingPage', {
      header: this.translate.instant('page.sendStagedPage.sencillitoTitle'),
      primaryButtonText: this.translate.instant('page.sendStagedPage.sencillitoButton'),
      primaryButtonAction: null,
      isSencillito: true,
      steps:[
        {text: 'page.sendStagedPage.step1Online', img: 'assets/img/steps-payment-pending/step1.svg'},
        {text: 'page.sendStagedPage.step2Online', img: 'assets/img/steps-payment-pending/step2.svg'},
        {text: 'page.sendStagedPage.step3Online', img: 'assets/img/steps-payment-pending/step3.svg', ean: true},
        {text: 'page.sendStagedPage.step4Online', img: 'assets/img/steps-payment-pending/step4.svg'}]
    });
  }

  get ModalAlertPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage')
  }

  get OptionSelectorPage(): INavInfo {
    return this.getNavInfo('OptionSelectorPage')
  }

  get ReviewPromptPage(): INavInfo {
    return this.getNavInfo('ReviewPromptPage')
  }

  get NpsSurveyPage(): INavInfo {
    return this.getNavInfo('NpsSurveyPage')
  }

  get LocationSearchPage(): INavInfo {
    return this.getNavInfo('LocationSearchPage')
  }

  get PlaidErrorDetailPage(): INavInfo {
    return this.getNavInfo('PlaidErrorDetailPage');
  }

  get LocationDetailPage(): INavInfo {
    return this.getNavInfo('LocationDetailPage');
  }

  get StagedRiaBrandingPage(): INavInfo {
    return this.getNavInfo('StagedRiaBrandingPage');
  }

  get BranchLookupPage(): INavInfo {
    return this.getNavInfo('BranchLookupPage');
  }

  get AddPromoPage(): INavInfo {
    return this.getNavInfo('AddPromoPage');
  }

  get AddAmountPage(): INavInfo {
    return this.getNavInfo('AddAmountPage');
  }

  get CancellationReasonsPage(): INavInfo {
    return this.getNavInfo('CancellationReasonsPage');
  }

  get SendPaymentPendingPayInStore(): INavInfo {
    return this.getNavInfo('SendPaymentpendingPage', {
      header: this.translate.instant('page.sendStagedPage.sencillitoTitle'),
      primaryButtonText: this.translate.instant('page.sendStagedPage.showBarcodeButton'),
      primaryButtonAction: null,
      secondaryButtonText: this.translate.instant('global.myRia.jsonContent.CheckLocations'),
      secondaryButtonAction: (routeProvider) => routeProvider.push(routeProvider.pages.StoresPageNew),

      steps:[
        {text: 'page.sendStagedPage.step1InPerson', img: 'assets/img/steps-payment-pending/step1.svg',
          stores: ['assets/img/steps-payment-pending/store1.svg', 'assets/img/steps-payment-pending/store2.svg',
                   'assets/img/steps-payment-pending/store3.svg', 'assets/img/steps-payment-pending/store4.svg']},
        {text: 'page.sendStagedPage.step2InPerson', img: 'assets/img/steps-payment-pending/step2.svg'},
        {text: 'page.sendStagedPage.step3InPerson', img: 'assets/img/steps-payment-pending/step3.svg'}]
    });
  }

  get SendEpayBarcodePage(): INavInfo {
    return this.getNavInfo('SendEpayBarcodePage', {
      steps:[
        {text: 'page.sendStagedPage.step1Epay', img: 'assets/img/steps-payment-pending/step1.svg'},
        {text: 'page.sendStagedPage.step2Epay', img: 'assets/img/steps-payment-pending/step2.svg'},
        {text: 'page.sendStagedPage.step3Epay', img: 'assets/img/steps-payment-pending/step3.svg'}]
    });
  }

  get SendPaymentPendingPayID(): INavInfo {
    return this.getNavInfo('SendPaymentpendingPage', {
      header: this.translate.instant('page.sendStagedPage.payIDTitle'),
      additionalText:[
        {text: 'page.sendStagedPage.fundsReleasedContent'},
        {link: 'page.sendStagedPage.payIDLearnMoreLink'}],
      steps:[
        {text: 'page.sendStagedPage.step1PayID', img: 'assets/img/steps-payment-pending/step1.svg'},
        {text: 'page.sendStagedPage.step2PayID', img: 'assets/img/steps-payment-pending/step2.svg', ean: true},
        {text: 'page.sendStagedPage.step3PayID', img: 'assets/img/steps-payment-pending/step3.svg'}]
    });
  }

  get SendPaymentPendingPayNow(): INavInfo {
    return this.getNavInfo('SendPayNowPage', {
      header: this.translate.instant('page.sendStagedPage.payIDTitle'),
      additionalText:[]
    });
  }

  get SendAdditionalFieldsPage(): INavInfo {
    return this.getNavInfo('SendAdditionalFieldsPage', {
      customerFields: null,
      recipientFields: null,
      onContinue: null
    });
  }

  get SendCvvPage(): INavInfo {
    return this.getNavInfo('SendCvvPage', {
      onContinue: null,
      SendCvvPage: true
    });
  }

  get CvvInfoPage(): INavInfo {
    return this.getNavInfo('CvvInfoPage');
  }

  get BrebConfirmationPage(): INavInfo {
    return this.getNavInfo('BrebConfirmationPage');
  }

  get SendPayNearMePage(): INavInfo {
    return this.getNavInfo('SendPayNearMePage');
  }

  get SendUpdateProfilePage(): INavInfo {
    return this.getNavInfo('SettingsPersonalPage', {
      onContinue: null,
      SettingsPersonalPage: true
    });
  }

  get SendUpdateIdPage(): INavInfo {
    return this.getNavInfo('SettingsIdPage', {
      onContinue: null,
      SettingsIdPage: true
    });
  }

  get SendToRecipientsPage(): INavInfo {
    return this.getNavInfo('RecipientListPage', {
      header: this.translate.instant('page.recipientListPage.title'),
      onAdd: null,
      onSelect: null,
      country: null,
      recipients: null,
      showSelectedRecipient: true
    });
  }

  get SendToNewRecipientPage(): INavInfo {
    return this.getNavInfo('RecipientDetailsPage', {
      country: null,
      onCreated: null
    });
  }

  get SendToBankDepositPage(): INavInfo {
    return this.getNavInfo('BankDepositPage', {
      recipient: null,
      sendFromRecipient: false,
    });
  }

  get SendToBankSearchPage(): INavInfo {
    return this.getNavInfo('BankSearchPage', {
      recipient: null,
    });
  }

  get SendToDebitCardPayoutPage(): INavInfo {
    return this.getNavInfo('DebitCardPayoutPage', {
      recipient: null,
      sendFromRecipient: false,
    });
  }

  get WalletDetailsPage(): INavInfo {
    return this.getNavInfo('WalletDetailsPage');
  }

  get MobileWalletDetails(): INavInfo {
    return this.getNavInfo('MobileWalletDetailsPage');
  }

  get SendToLocationPage(): INavInfo {
    return this.getNavInfo('LocationListPage', {
      search: null,
      country: null,
      deliveryMethod: null,
      onSelect: null,
      showSelectedLocation: true
    });
  }

  get SendToLocationSelectPage(): INavInfo {
    return this.getNavInfo('LocationSelectListPage', {
      agent: null,
      amount: null,
      currency: null,
      deliveryMethod: null,
      onSelect: null,
      showSelectedLocation: true
    });
  }

  get SendWithPaymentsPage(): INavInfo {
    return this.getNavInfo('PaymentAccountListPage', {
      header: this.translate.instant('page.paymentAccountListPage.title'),
      onAdd: null,
      onSelect: null,
      showSelectedAccount: true
    });
  }

  get SendWithNewBankAccountPage(): INavInfo {
    return this.getNavInfo('BankAccountVerificationPage', {
      paymentMethod: null,
      onCreated: null
    });
  }

  get SendWithNewPaymentPage(): INavInfo {
    return this.getNavInfo('PaymentAccountDetailsPage', {
      header: this.translate.instant('page.paymentAccountDetailsPage.title'),
      paymentMethod: null,
      onCreated: null
    });
  }

  get SendAgainPage(): INavInfo {
    return this.getNavInfo('SendSummaryPage', {
      sendAgain: true,
      orderPromise: null,
      SendSummaryPage: true
    });
  }

  get SendAgainPaymentAccountPage(): INavInfo {
    return this.getNavInfo('PaymentAccountListPage', {
      orderPromise: null,
      PaymentAccountListPage: true
    });
  }

  get SendSummaryPage(): INavInfo {
    return this.getNavInfo('SendSummaryPage');
  }

  get SendSummaryNotLoggedInPage(): INavInfo {
    return this.getNavInfo('SendSummaryPage', {
      onLogin: null
    });
  }

  get IdentityVerificationPage(): INavInfo {
    return {
      page: 'IdentityVerificationPage',
      params: {
        onLogin: null
      }
    }
  }

  get showErrorPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
        img: 'assets/imgs/Warning.svg',
        showFullPage: true,
    });
  }

  get RealTimeModifyFailedPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
        showClose: true,
        goToActivity: true,
        img: 'assets/imgs/Warning.svg',
        title: this.translate.instant('global.recipientChangeErrorHeader'),
        msg: this.translate.instant('global.recipientChangeErrorContent'),
        primaryButtonText: this.translate.instant('page.paymentAccountVerifyPage.callCustomerServiceButton'),
        contactCTAReason: ContactModalReasons.ModifyRecipientNameNotAccepted,
        openChatWidgetIfAvailable: true,
        goContact: true,
        showFullPage: true,
    });
  }

  get OnHoldUserFailedPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
        showClose: true,
        goToActivity: true,
        img: 'assets/imgs/Warning.svg',
        title: this.translate.instant('global.requestProblemErrorHeader'),
        msg: this.translate.instant('global.requestProblemErrorContent'),
        contactCTAReason: ContactModalReasons.ModifyRecipientNameNotAvailableCustomerOnHold,
        primaryButtonText: this.translate.instant('page.paymentAccountVerifyPage.callCustomerServiceButton'),
        openChatWidgetIfAvailable: true,
        goContact: true,
        showFullPage: true,
    });
  }


  get FirstPayInStorePage(): INavInfo {
    return this.getNavInfo('PayInStoreInfoPage', {
        img: 'assets/imgs/Identity.svg',
        title: this.translate.instant('page.trackReviewDetailsPage.transactionNeedKYCHeader'),
        msg: this.translate.instant('page.trackReviewDetailsPage.transactionNeedKYCContent'),
        subMsg: this.translate.instant('page.trackReviewDetailsPage.needValidIDText'),
        primaryButtonText: this.translate.instant('global.genericContinueButton'),
    });
  }

  get FirstPayInStoreOrFPXPage(): INavInfo {
    return this.getNavInfo('PayInStoreInfoPage', {
        img: 'assets/imgs/Identity.svg',
        title: this.translate.instant('page.trackReviewDetailsPage.transactionFPXNeedKYCHeader'),
        msg: this.translate.instant('page.trackReviewDetailsPage.transactionNeedKYCContent'),
        subMsg: this.translate.instant('page.trackReviewDetailsPage.needValidIDText'),
        primaryButtonText: this.translate.instant('global.genericContinueButton'),
    });
  }

  get IdExpiredPayInStorePage(): INavInfo {
    return this.getNavInfo('PayInStoreInfoPage', {
        img: 'assets/imgs/Identity.svg',
        title: this.translate.instant('page.trackReviewDetailsPage.transactionIDExpiredHeader'),
        msg: this.translate.instant('page.trackReviewDetailsPage.transactionIDExpiredContent'),
        subMsg: this.translate.instant('page.trackReviewDetailsPage.needValidIDText'),
        primaryButtonText: this.translate.instant('global.genericContinueButton'),
    });
  }

  get PayInStoreVisa1or2MYPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      img: 'assets/imgs/B&M-logo2.svg',
      title: this.translate.instant('page.sendRestrictionsPage.payInPersonText'),
      primaryButtonText: this.translate.instant('page.sendRestrictionsPage.payInPersonText'),
      showFullPage: true,
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant('page.sendFailedPage.goBackButton'),
  });
  }

  get PayInStoreVisa3MYPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      img: 'assets/imgs/B&M-logo2.svg',
      title: this.translate.instant('page.sendRestrictionsPage.payInPersonText'),
      showFullPage: true,
      primaryButtonText: this.translate.instant('page.sendRestrictionsPage.payInPersonText'),
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant('page.sendFailedPage.goBackButton'),
  });
  }

  get PayInCorrectMultiplesStagingPLPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      img: 'assets/imgs/atm.svg',
      title: this.translate.instant('page.cashReminderPage.cashPaymentTitle'),
      msg: this.translate.instant('page.cashReminderPage.cashPaymentText'),
      showFullPage: true,
      primaryButtonText: this.translate.instant('global.genericContinueButton'),
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant('page.sendMoneyAdditionalDetailsPage.changeAmountButton'),
  });
  }

  get IdVerificationNeededPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      img: 'assets/imgs/Identity.svg',
      title: this.translate.instant('page.sendMoneyAdditionalDetailsPage.additionalDetailsIdentityVerificationHeader'),
      msg: this.translate.instant('page.sendMoneyAdditionalDetailsPage.additionalDetailsDocumentsPending'),
      showFullPage: true,
      primaryButtonText: this.translate.instant('global.genericContinueButton'),
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant('page.sendMoneyPaymentPage.payInStoreButton'),
    });
  }

  get SendSummaryContinuePage(): INavInfo {
    return this.getNavInfo('SendSummaryPage', {
      onContinue: null,
      SendSummaryPage: true
    });
  }

  get SendFinishPage(): INavInfo {
    return this.getNavInfo('SendFinishPage');
  }

  get SendFailedPage(): INavInfo {
    return this.getNavInfo('SendFailedPage', {
      error: null,
      missing: null
    });
  }

  get SendPspPage(): INavInfo {
    return this.getNavInfo('SendPspPage');
  }

  get SendPayFundsPage(): INavInfo {
    return this.getNavInfo('SendPayFundsPage');
  }

  get SendDisabledPage(): INavInfo {
    return this.getNavInfo('SigninPage', {
      error: null,
      backPage: this.PreLoginPage
    });
  }

  get SendRequireVerification(): INavInfo {
    return this.getNavInfo('SendFailedPage', {
      missing: MissingTransactionInfoType.RequiresIdentityVerification,
      onContinue: null
    });
  }

  get SendRequireHigherLimitVerification(): INavInfo {
    return this.getNavInfo('SendFailedPage', {
      missing: MissingTransactionInfoType.HigherLimitIdentityVerificationRequired,
      onContinue: null});
  }

  get VeriffErrorCodePage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showRightClose: true,
      img: 'assets/imgs/warning-new.svg',
      title: this.translate.instant('page.sendMoneyAdditionalDetailsPage.additionalDetailsIdentityFailError'),
      msg: this.translate.instant('page.sendMoneyAdditionalDetailsPage.additionalDetailsIdentityFailErrorText'),
      primaryButtonText: this.translate.instant('page.sendMoneyAdditionalDetailsPage.additionalDetailsIdentityFailTryAgain'),
      primaryButtonAction: null
    });
  }

  get SendPayFpxFailed(): INavInfo {
    return this.getNavInfo('SendFailedPage', {
      errorTitle: this.translate.instant('page.sendFailedPage.fpxSendFailedHeader'),
      errorDetails: this.translate.instant('page.sendFailedPage.fpxSendFailedContent'),
      buttonLeft: this.translate.instant('page.sendFailedPage.goBackButton'),
      newDesign: true,
    });
  }

  get SendPayFintocFailed(): INavInfo {
    return this.getNavInfo('SendFailedPage', {
      errorTitle: this.translate.instant('PageSendMoneyFintoc.ModalTryAgainInfoHeader'),
      errorDetails: this.translate.instant('PageSendMoneyFintoc.ModalTryAgainInfoMessage'),
      buttonLeft: this.translate.instant('global.tryAgainButton'),
      buttonRight: this.translate.instant('page.sendCompletedPage.activityButton'),
      goToActivitySecondaryBtn: true,
      newDesign: true,
      removeHeader: true,
    });
  }

  get SendPayIDealFailed(): INavInfo {
    return this.getNavInfo('SendFailedPage', {
      errorTitle: this.translate.instant('page.sendFailedPage.iDEALSendFailedHeader'),
      errorDetails: this.translate.instant('page.sendFailedPage.iDEALSendFailedContent'),
      buttonLeft: this.translate.instant('page.sendCompletedPage.activityButton'),
      buttonRight: this.translate.instant('global.tryAgainButton'),
      goToActivity: true,
      newDesign: true,
    });
  }

  get SendPayBancontactFailedPage(): INavInfo {
    return this.getNavInfo('SendFailedPage', {
      errorTitle: this.translate.instant('page.sendFailedPage.bancontactSendFailedHeader'),
      errorDetails: this.translate.instant('page.sendFailedPage.bancontactSendFailedBody'),
      buttonLeft: this.translate.instant('page.sendCompletedPage.activityButton'),
      buttonRight: this.translate.instant('global.tryAgainButton'),
      goToActivity: true,
      newDesign: true,
    });
  }

  get SendPayGiropayFailedPage(): INavInfo {
    return this.getNavInfo('SendFailedPage', {
      errorTitle: this.translate.instant('page.sendFailedPage.giropaySendFailedHeader'),
      errorDetails: this.translate.instant('page.sendFailedPage.giropaySendFailedBody'),
      buttonLeft: this.translate.instant('page.sendCompletedPage.activityButton'),
      buttonRight: this.translate.instant('global.tryAgainButton'),
      goToActivity: true,
      newDesign: true,
    });
  }

  get SendVerifyPage(): INavInfo {
    return this.getNavInfo('SendVerifyPage', {
      identityPromise: null
    });
  }

  get SendCompletedPage(): INavInfo {
    return this.getNavInfo('SendCompletedPage');
  }

  get SendReceiptPage(): INavInfo {
    return this.getNavInfo('SendReceiptPage');
  }

  get SendPaymentAuthPage(): INavInfo {
    return this.getNavInfo('SendPaymentAuthPage');
  }

  get SendStatePage(): INavInfo {
    return this.getNavInfo('SendStatePage');
  }

  get FailedOCR(): INavInfo {
    return this.getNavInfo('SendFailedPage', {
      OCRFail: true
    });
  }

  get PaymentAccountVerifyCheckPage(): INavInfo {
    return this.getNavInfo('PaymentAccountVerifyCheckPage', {
      bank: null,
      onContinue: null,
      pageName: 'PaymentAccountVerifyCheckPage'
    });
  }

  get PaymentAccountVerifyPage(): INavInfo {
    return this.getNavInfo('PaymentAccountVerifyPage', {
      bank: null,
      onContinue: null,
      pageName: 'PaymentAccountVerifyPage'
    });
  }

  get PaymentAccountVerifiedPage(): INavInfo {
    return this.getNavInfo('PaymentAccountVerifiedPage', {
    });
  }

  get PaymentAccountDisabledPage(): INavInfo {
    return this.getNavInfo('PaymentAccountVerifyFailPage', {
      img: 'assets/icons/build-fail.svg',
      header: this.translate.instant('page.paymentAccountVerifyPage.bankAccountDisabledHeader'),
      content: this.translate.instant('page.paymentAccountVerifyPage.bankAccountDisabledContent')
    });
  }

  get PaymentAccountCantVerifyFailPage(): INavInfo {
    return this.getNavInfo('PaymentAccountVerifyFailPage', {
      img: 'assets/imgs/Warning.svg',
      header: this.translate.instant('page.paymentAccountVerifyPage.bankAccountCantVerifyTitle'),
      content: this.translate.instant('page.paymentAccountVerifyPage.bankAccountCantVerifyContent')
    });
  }

  get BankDepositPage(): INavInfo {
    return this.getNavInfo('BankDepositPage');
  }

  get DebitCardPayoutPage(): INavInfo {
    return this.getNavInfo('DebitCardPayoutPage');
  }

  get DebitCardPayoutInfoPage(): INavInfo {
    return this.getNavInfo('DebitCardPayoutInfoPage');
  }

  get OrderDetailsPage(): INavInfo {
    return this.getNavInfo('SendReceiptPage', {
      orderPromise: null,
      showEvents: false
    });
  }

  get HrefPage(): INavInfo {
    return this.getNavInfo('HrefPage', {
      header: null,
      href: null
    });
  }

  get LocationMapPage(): INavInfo {
    return this.getNavInfo('LocationMapPage', {
      address: null
    });
  }

  get PaymentAccountDetailsPage(): INavInfo {
    return this.getNavInfo('PaymentAccountDetailsPage');
  }

  get BankAccountVerificationPage(): INavInfo {
    return this.getNavInfo('BankAccountVerificationPage');
  }

  get PaymentMethodAddSelectPage(): INavInfo {
    return this.getNavInfo('PaymentMethodAddSelectPage');
  }

  // get FindBankPlaidFailedPage(): INavInfo{
  //   return this.getNavInfo('FindBankPlaidFailedPage',{
  //     addBank:null
  //   });
  // }

  get TrackPage(): INavInfo {
    return this.getNavInfo('DashboardPage', {}, false, 'custom-icon-tab-track', 'global.mainNavigation.trackTransferButton');
  }

  get SendPage(): INavInfo {
    return this.getNavInfo('CheckPricesPage', {}, false, 'custom-icon-tab-send', 'global.mainNavigation.sendMoneyNavigation');
  }

  get FindRiaLocationsPage(): INavInfo {
    return this.getNavInfo('StoreLocatorPage', {}, false, 'custom-icon-tab-locations', 'global.mainNavigation.locationsButton', 'storeTabBtn'); // this used because when page with empty string it will cause an error and will not open
  }
  get StoresPage(): INavInfo {
    let page = 'StoreLocatorPage';
    // empty string since we need the icon but do not want to open the revamp page in ion-tabs
    if (APP_CONFIG.featureFlags.findLocationRevamp)
      page = 'StoreLocatorNewPage';
    return this.getNavInfo(page, {}, false, 'custom-icon-tab-locations', 'global.mainNavigation.locationsButton', 'storeTabBtn');
  }
  get StoresEmbeddedPage(): INavInfo {
    return this.getNavInfo('StoreLocatorEmbeddedPage', {}, false, 'custom-icon-tab-locations', 'global.mainNavigation.locationsButton', 'storeTabBtn');
  }

  get StoresPageNew(): INavInfo {
    let page = 'StoreLocatorNewPage';
    return this.getNavInfo(page, {}, false, 'custom-icon-tab-locations', 'global.mainNavigation.locationsButton', 'storeTabBtn');
  }

  get HubPage(): INavInfo {
    return this.getNavInfo('HubPage', {}, false, 'custom-icon-tab-hub', 'global.mainNavigation.hubNav');
  }

  get SettingsPage(): INavInfo {
    return this.getNavInfo('SettingsPage', {}, false, 'md-settings', 'global.mainNavigation.myAccountNavigation');
  }

  get SettingsPersonalPage(): INavInfo {
    return this.getNavInfo('SettingsPersonalPage');
  }

  get RequirePasswordChangePage(): INavInfo {
    return this.getNavInfo('SettingsPasswordPage');
  }

  get SettingsPasswordPage(): INavInfo {
    return this.getNavInfo('SettingsPasswordPage');
  }

  get SettingsLegalPage(): INavInfo {
    return this.getNavInfo('SettingsLegalPage');
  }

  get SettingsNotificationsPage(): INavInfo {
    return this.getNavInfo('SettingsNotificationsPage');
  }

  get SettingsIdPage(): INavInfo {
    return this.getNavInfo('SettingsIdPage');
  }

  get OrderCancelledPage(): INavInfo {
    return this.getNavInfo('OrderCancelledPage');
  }

  get HelpPage(): INavInfo {
    return this.getNavInfo('HelpPage');
  }

  get SelectPickerPage(): INavInfo {
    return this.getNavInfo('SelectPickerPage');
  }

  get AddRecipientPage(): INavInfo {
    return this.getNavInfo('AddRecipientPage');
  }

  get TransferSummaryPage(): INavInfo {
    return this.getNavInfo('TransferSummaryPage');
  }

  get RegistrationPage(): INavInfo {
    return this.getNavInfo('RegistrationStep1Page');
  }

  get RegistrationStepOtpPage(): INavInfo {
    return this.getNavInfo('RegistrationStepOtpPage');
  }

  get RegistrationStepUsernamePage(): INavInfo {
    return this.getNavInfo('RegistrationStepUsernamePage');
  }

  get RegistrationStepPasswordPage(): INavInfo {
    return this.getNavInfo('RegistrationStepPasswordPage');
  }

  get RegistrationStepBmPage(): INavInfo {
    return this.getNavInfo('RegistrationStepBmPage');
  }

  get RegistrationStepBmLink(): INavInfo {
    return this.getNavInfo('RegistrationStepBmLinkPage');
  }

  get RegistrationStepBmFailed(): INavInfo {
    return this.getNavInfo('RegistrationStepBmFailedPage');
  }

  get RegistrationStepBmFieldsFailed(): INavInfo {
    return this.getNavInfo('ModalAlertPage', { showFullPage: true });
  }

  get RegistrationStepDynamicPage(): INavInfo {
    return this.getNavInfo('RegistrationStepDynamicPage', {
      step: 0
    });
  }

  get RegistrationStepDynamicAddressPage(): INavInfo {
    return this.getNavInfo('RegistrationStepDynamicPage', null, null, null, null, 'registration-step-address');
  }

  get AddressVerifyPage(): INavInfo {
    return this.getNavInfo('AddressVerifyPage');
  }

  get RegistrationDeeplinkingStepPage():INavInfo{
    return this.getNavInfo('RegistrationDeeplinkingStepPage');
  }
  get MobileVerifyPage(): INavInfo {
    return this.getNavInfo('MobileVerifyPage');
  }

  get LoginStepDynamicPage(): INavInfo {
    return this.getNavInfo('LoginStepDynamicPage', {
      step: 0
    });
  }

  get RegistrationStepVerifyFailed(): INavInfo {
    return this.getNavInfo('RegistrationStepVerifyFailedPage', {
      onBack: (routeProvider) => {
        if (routeProvider.hasPage(routeProvider.pages.SigninPage, true))
          routeProvider.popTo(routeProvider.pages.SigninPage);
        else
          routeProvider.setRoot(routeProvider.pages.PreLoginPage);
      }
    });
  }

  get SignicatVerifyPage(): INavInfo {
    return this.getNavInfo('SignicatVerifyPage');
  }

  get RegistrationStepSecurityQuestionPage(): INavInfo {
    return this.getNavInfo('RegistrationStep4Page');
  }

  get RegistrationStep1Page(): INavInfo {
    return this.getNavInfo('RegistrationStep1Page');
  }

  get RegistrationStep2Page(): INavInfo {
    return this.getNavInfo('RegistrationStep2Page');
  }

  get RegistrationStep3Page(): INavInfo {
    return this.getNavInfo('RegistrationStep3Page');
  }

  get RegistrationFinishPage(): INavInfo {
    return this.getNavInfo('RegistrationFinishPage');
  }

  get RegistrationFailedPage(): INavInfo {
    return this.getNavInfo('RegistrationFailedPage', {
      error: null,
      RegistrationFailedPage: true
    });
  }

  get RegistrationCustomerLinkingQuestionnairePage(): INavInfo {
    return this.getNavInfo('RegistrationCustomerLinkingQuestionnairePage');
  }

  get RegistrationBiometricsPage(): INavInfo {
    return this.getNavInfo('RegistrationBiometricsPage');
  }

  get FeedbackPage(): INavInfo {
    return this.getNavInfo('FeedbackPage');
  }

  get EmailUpdatePage(): INavInfo {
    return this.getNavInfo('EmailUpdatePage');
  }

  get TrackTransferPage(): INavInfo {
    return this.getNavInfo('TrackTransferPage', {}, false, 'custom-icon-tab-track', 'global.mainNavigation.trackTransferButton','trackTabBtn');
  }

  get SendFraudAlertPage(): INavInfo {
    return this.getNavInfo('SendFraudAlertPage');
  }

  get SendFraudAlertNewPage(): INavInfo{
    return this.getNavInfo('SendFraudAlertNewPage');
  }

  get ScamAlertPopupPage(): INavInfo {
    return this.getNavInfo('ScamAlertPopupPage');
  }

  get ScamAlertPickerPage(): INavInfo {
    return this.getNavInfo('ScamAlertPickerPage');
  }

  get CardVerificationRequiredPage(): INavInfo{
    return this.getNavInfo('CardVerificationRequiredPage');
  }

  get SendBarcode(): INavInfo {
    return this.getNavInfo('SendBarcodePage');
  }

  get InfoPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', { showFullPage: true });
  }

  get VerifyAuDrivingLicensePage() {
    return this.getNavInfo('VerifyAuDrivingLicensePage');
  }

  get ComplianceQuestionsPage(): INavInfo {
    return this.getNavInfo('ComplianceQuestionsPage');
  }

  get FinishPage(): INavInfo {
    return this.getNavInfo('FinishPage');
  }

  get WaitingPage(): INavInfo {
    return this.getNavInfo('WaitingPage');
  }

  get RegistrationDeeplinkingPreparePage(): INavInfo {
    return this.getNavInfo('RegistrationDeeplinkingPreparePage');
  }

  get RegistrationDeeplinkingWelcomePage(): INavInfo {
    return this.getNavInfo('RegistrationDeeplinkingWelcomePage');
  }

  get OnfidoInfoPage(): INavInfo {
    let tips = [
      {text: 'page.sendMoneyAdditionalDetailsPage.docUploadContent.iDNotExpired'},
      {text: 'page.sendMoneyAdditionalDetailsPage.docUploadContent.pictureReadable'},
      {text: 'page.sendMoneyAdditionalDetailsPage.docUploadContent.iDNotCutOff'},
      {text: 'page.sendMoneyAdditionalDetailsPage.docUploadContent.iDCorrectSide'}];

    return this.getNavInfo('OnfidoInformationPage', {
      analyticsLocation: SegmentLocationNames.Settings,
      checkboxEnabled: true,
      header: this.translate.instant('page.sendMoneyAdditionalDetailsPage.verifyIdentityTitle'),
      msg: this.translate.instant('page.sendMoneyAdditionalDetailsPage.docUploadContent.makeSureLabel'),
      primaryButtonText: this.translate.instant('page.sendMoneyAdditionalDetailsPage.genericContinueButton'),
      tips: tips
      });
  }

  get OnfidoSelectDocument(): INavInfo {
    return this.getNavInfo('LinkCustomerOnfidoSelectDocumentPage');
  }

  // US wallet-onboarding Veriff flow — gates the redesigned eKYC result screens
  // so other US Veriff flows (send-money, settings) keep the legacy screens/copy.
  private get isUSWalletVeriffFlow(): boolean {
    return APP_CONFIG.region.region === Region.US
      && EkycFlowState.currentType === KYC_METHODS.VERIFF
      && EkycFlowState.isWalletOnboarding;
  }

  get OnfidoVerifiedPage(): INavInfo {
    if (this.isUSWalletVeriffFlow) {
      return this.getNavInfo('FinishPage', {
        usWalletResult: true,
        showHelp: false,
        showBack: false,
        img: 'assets/imgs/id-verified.svg',
        title: 'ID verified',
        msg: "Next, we'll ask for your tax ID to finish setting up your account.",
        primaryButtonText: 'Continue',
        primaryButtonAction: null
      });
    }
    return this.getNavInfo('FinishPage', {
      showHelp: true,
      showBack: true,
      img: 'assets/icons/onfido-verified.svg',
      title: this.translate.instant(APP_CONFIG.region.region === Region.NZ ? 'ekyc.veriffSuccess.title' : 'page.sendFinishPage.sendFinishVerifySuccessContent'),
      primaryButtonText: this.translate.instant('page.sendFinishPage.genericContinueButton'),
      primaryButtonAction: null
    });
  }

  get OnfidoProgressPage(): INavInfo {
    if (this.isUSWalletVeriffFlow)
      return this.usWalletPendingPage;
    return this.getNavInfo('ModalAlertPage', {
      showHelp: false,
      showBack: false,
      showRightClose: true,
      border: false,
      img: 'assets/imgs/Identity.svg',
      title: `page.activityPage.iDVerificationInProgressHeader`,
      msg: `page.activityPage.iDVerificationInProgressContent`,
      primaryButtonText: `global.gotItButton`,
      translateOnDestination: true,
      primaryButtonAction: null,
      showFullPage: true,
    });
  }

  get OnfidoPendingPage(): INavInfo {
    if (this.isUSWalletVeriffFlow)
      return this.usWalletPendingPage;
    return this.getNavInfo('ModalAlertPage', {
      showHelp: false,
      showBack: false,
      showRightClose: true,
      border: false,
      img: 'assets/imgs/Identity.svg',
      title: `page.activityPage.iDVerificationInProgressHeader`,
      msg: `page.activityPage.iDVerificationInProgressContent`,
      primaryButtonText: `global.gotItButton`,
      translateOnDestination: true,
      primaryButtonAction: null,
      showFullPage: true,
    });
  }

  // US Wallet (Veriff) "under review" pending result — light, hardcoded copy
  private get usWalletPendingPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      hideHeader: true,
      border: false,
      verticalCenter: true,
      customBodyClass: 'us-ekyc-result',
      img: 'assets/imgs/hourglass.svg',
      customeImgStyle: { width: '120px', height: 'auto' },
      title: 'Your information is under review',
      msg: "This is part of our standard security process. We'll notify you once it's complete.",
      primaryButtonText: 'Go Home',
      primaryButtonAction: (routeProvider) => routeProvider.setRoot(routeProvider.pages.TabsHomeActivityPage),
      showFullPage: true,
    });
  }

  // If from CA/UK and in SMF, then show different text and allow user to change sending amount
  get OnfidoNotVerifiedPageSMF(): INavInfo {
    var params;
    if (APP_CONFIG.region.region === Region.CA || APP_CONFIG.region.region === Region.UK) {
      params = {
        showRightClose: true,
        goToActivity: false,
        img: 'assets/imgs/exclamation_red.svg',
        title: this.translate.instant('page.identityVerificationPage.errorHeader'),
        msg: this.translate.instant('page.identityVerificationPage.lowerAmountBody'),
        hasSecondaryButton: (routeProvider) => routeProvider.hasPage(routeProvider.pages.CheckPricesPage),
        primaryButtonText: this.translate.instant('page.identityVerificationPage.identityVerificationChangeAmountButton'),
        secondaryButtonText: this.translate.instant('global.visitHelpCenterButton'),
        trackEvent: AnalyticsEventType.ContactModalDisplayed,
        contactCTAReason: ContactModalReasons.AccountBlockedIdVerification,
        trackParams: {
          location: SegmentLocationNames.SendMoney,
          reason: ContactModalReasons.AccountBlockedIdVerification
        },
        openChatWidgetIfAvailable: true,
        primaryButtonAction: (routeProvider) => routeProvider.popTo(routeProvider.pages.CheckPricesPage),
        showZendeskHelpPage: true,
        showFullPage: true,
      }
    }
    else if(APP_CONFIG.region.region === Region.AU)
      return this.AustraliaManualVerificationNeededPage;
    else {
      const msg = this.translate.instant(
          APP_CONFIG.region.region === Region.US ? 'page.walletReceiveMoneyPage.visitHelpCenterBody'
          : 'page.identityVerificationPage.visitHelpCenterBody');

      params = {
        showRightClose: true,
        goToActivity: false,
        img: 'assets/imgs/exclamation_red.svg',
        title: this.translate.instant('page.identityVerificationPage.errorHeader'),
        msg: msg,
        primaryButtonText: this.translate.instant('global.visitHelpCenterButton'),
        trackEvent: AnalyticsEventType.ContactModalDisplayed,
        contactCTAReason: ContactModalReasons.AccountBlockedIdVerification,
        openChatWidgetIfAvailable: true,
        trackParams: {
          location: SegmentLocationNames.SendMoney,
          reason: ContactModalReasons.AccountBlockedIdVerification
        },
        showZendeskHelpPage: true,
        showFullPage: true,
      }
    }
    return this.getNavInfo('ModalAlertPage', params);
  }

  get OnfidoNotVerifiedPageNonSMF(): INavInfo {
    if (APP_CONFIG.region.region === Region.AU)
      return this.AustraliaManualVerificationNeededPage;
    if (this.isUSWalletVeriffFlow) {
      return this.getNavInfo('ModalAlertPage', {
        hideHeader: true,
        verticalCenter: true,
        customBodyClass: 'us-ekyc-result',
        img: 'assets/imgs/id-rejected.svg',
        customeImgStyle: { width: '120px', height: 'auto' },
        title: 'Unable to verify your identity',
        msg: 'Please contact us for manual verification',
        primaryButtonText: 'Contact us',
        showZendeskHelpPage: true,
        showSecondaryButton: true,
        secondaryButtonText: 'Go Home',
        secondaryButtonAction: (routeProvider) => routeProvider.setRoot(routeProvider.pages.TabsHomeActivityPage),
        trackEvent: AnalyticsEventType.ContactModalDisplayed,
        contactCTAReason: ContactModalReasons.AccountBlockedIdVerification,
        trackParams: {
          location: SegmentLocationNames.SendMoney,
          reason: ContactModalReasons.AccountBlockedIdVerification
        },
        showFullPage: true
      });
    }
    else return this.getNavInfo('ModalAlertPage', {
      showRightClose: true,
      goToActivity: false,
      img: 'assets/imgs/exclamation_red.svg',
      title: this.translate.instant('page.identityVerificationPage.errorHeader'),
      msg: this.translate.instant('page.identityVerificationPage.visitHelpCenterBody'),
      primaryButtonText: this.translate.instant('global.visitHelpCenterButton'),
      trackEvent: AnalyticsEventType.ContactModalDisplayed,
      contactCTAReason: ContactModalReasons.AccountBlockedIdVerification,
      openChatWidgetIfAvailable: true,
      trackParams: {
        location: SegmentLocationNames.SendMoney,
        reason: ContactModalReasons.AccountBlockedIdVerification
      },
      showZendeskHelpPage: true,
      showFullPage: true
    });
  }

  get OnfidoRejectedPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showBack: false,
      showRightClose: true,
      img: 'assets/imgs/Warning.svg',
      title: this.translate.instant('page.activityPage.issueWithIDHeader'),
      primaryButtonText: this.translate.instant('global.tryAgainButton'),
      showFullPage: true,
    });
  }

  get NewPasswordPage(): INavInfo {
    return this.getNavInfo('SigninNewPasswordPage');
  }

  get SigninForgotVerifyBirthdatePage(): INavInfo {
    return this.getNavInfo('SigninForgotVerifyBirthdatePage');
  }

  get LoginVerifyBirthdatePage(): INavInfo {
    return this.getNavInfo('LoginVerifyBirthdatePage');
  }

  get OnfidoServerErrorPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showHelp: true,
      border: false,
      img: 'assets/imgs/Warning.svg',
      title: this.translate.instant('page.identityVerificationPage.identityVerificationHeaderVerify'),
      msg: this.translate.instant('page.identityVerificationPage.identityVerificationContentVerify'),
      primaryButtonText: this.translate.instant('page.identityVerificationPage.identityVerificationContinueButton'),
      primaryButtonAction: null,
    });
  }

  get SignicatAttemptsExceededPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showRightClose: true,
      img: 'assets/imgs/Warning.svg',
      title: this.translate.instant('page.BankIdError.title'),
      msg: this.translate.instant('page.BankIdError.description'),
      primaryButtonText: this.translate.instant('page.BankIdError.button'),
      primaryButtonAction: null
    });
  }

  get OnfidoExpirationErrorPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      border: false,
      img: 'assets/imgs/Warning.svg',
      title: this.translate.instant('page.sendMoneyAdditionalDetailsPage.docUploadContent.docExpiredHeader'),
      msg: this.translate.instant('page.sendMoneyAdditionalDetailsPage.docUploadContent.tryAnotherDocContent'),
      primaryButtonText: this.translate.instant('page.sendMoneyAdditionalDetailsPage.docUploadContent.tryAgainButton'),
      primaryButtonAction: null,
      showCircle: true,
      newFooterStyle: true,
      showRightClose: true,
      goToActivity: true
    });
  }

  get DeepLinkExpiredTokenPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      img: 'assets/imgs/Warning.svg',
      title: this.translate.instant('page.preLoginPage.expiredCodeHeader'),
      msg: this.translate.instant('page.preLoginPage.expiredCodeContent'),
      primaryButtonText: this.translate.instant('global.gotItButton'),
      secondaryButtonText: this.translate.instant('global.callCustomerServiceButton'),
      hasSecondaryButton: true
    });
  }

  get OnfidoRejectedErrorPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      img: 'assets/imgs/Warning.svg',
      title: this.translate.instant('page.sendMoneyAdditionalDetailsPage.docUploadContent.docNotSupportedHeader'),
      msg: this.translate.instant('page.sendMoneyAdditionalDetailsPage.docUploadContent.tryAnotherDocContent'),
      primaryButtonText: this.translate.instant('page.sendMoneyAdditionalDetailsPage.docUploadContent.tryAgainButton'),
      primaryButtonAction: null,
      showCircle: true,
      newFooterStyle: true,
      showRightClose: true,
      goToActivity: true
    });
  }

  get OnfidoImageIntegrityErrorPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      img: 'assets/imgs/Warning.svg',
      title: this.translate.instant('page.sendMoneyAdditionalDetailsPage.docUploadContent.docNotReadableHeader'),
      tipsTitle: this.translate.instant('page.sendMoneyAdditionalDetailsPage.docUploadContent.makeSureLabel'),
      primaryButtonText: this.translate.instant('page.sendMoneyAdditionalDetailsPage.docUploadContent.tryAgainButton'),
      showCircle: true,
      newFooterStyle: true,
      tips: [
        {text: 'page.sendMoneyAdditionalDetailsPage.docUploadContent.pictureReadable'},
        {text: 'page.sendMoneyAdditionalDetailsPage.docUploadContent.iDNotCutOff'},
        {text: 'page.sendMoneyAdditionalDetailsPage.docUploadContent.iDCorrectSide'}]
    });
  }

  get PostePayCardInstructionsPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showBack: true,
      title: this.translate.instant('page.paymentAccountDetailsPage.enablePostepayInstructionsTitle'),
      alertText: this.translate.instant('page.paymentAccountDetailsPage.enablePostepayInstructionsAlert'),
      tipsTitle: this.translate.instant('page.paymentAccountDetailsPage.enablePostepayInstructionsListTitle'),
      primaryButtonText: this.translate.instant('page.paymentAccountDetailsPage.mainButton'),
      newFooterStyle: true,
      greyBackgroundTipsStyle: true,
      hideImage: true,
      tips: [
        {text: 'page.paymentAccountDetailsPage.enablePostepayInstructionsListBulletOne'},
        {text: 'page.paymentAccountDetailsPage.enablePostepayInstructionsListBulletTwo'},
        {text: 'page.paymentAccountDetailsPage.enablePostepayInstructionsListBulletThree'},
        {text: 'page.paymentAccountDetailsPage.enablePostepayInstructionsListBulletFour'},
        {text: 'page.paymentAccountDetailsPage.enablePostepayInstructionsListBulletFive'},
        {text: 'page.paymentAccountDetailsPage.enablePostepayInstructionsListBulletSix'},
      ]
    });
  }

  get SendPspSecurityCheckRequiredPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showBack: true,
      img: 'assets/imgs/security_check_emoji.svg',
      title: this.translate.instant('page.paymentMethods.securityCheck.title'),
      msg: this.translate.instant('page.paymentMethods.securityCheck.body'),
      newFooterStyle: true,
      primaryButtonText: this.translate.instant('page.paymentMethods.goToAuthentication.button'),
      primaryButtonAction: null,
      hasSecondaryButton: true,
      dismissFromSecondary: true,
      secondaryButtonText: this.translate.instant('page.sendFailedPage.goBackButton'),
      secondaryButtonAction: null,
    });
  }

  get HeadToStorePage(): INavInfo { // currently only for NZ
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showBack: false,
      img: 'assets/imgs/pay-in-store-building.svg',
      title: this.translate.instant('page.identityVerificationPage.findLocationHeader'),
      msg: this.translate.instant('page.identityVerificationPage.findLocationBody'),
      newFooterStyle: true,
      primaryButtonText: this.translate.instant('global.myRia.jsonContent.FindLocation'),
      primaryButtonAction: (routeProvider) => routeProvider.replace(routeProvider.pages.StoresPageNew),
    });
  }

  get UnableToVerifyGoToStorePage(): INavInfo { // currently only for NZ
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showBack: true,
      showHelp: true,
      img: 'assets/imgs/exclamation_red.svg',
      title: this.translate.instant('page.identityVerificationPage.errorHeader'),
      msg: this.translate.instant('page.identityVerificationPage.checkFailed'),
      newFooterStyle: true,
      primaryButtonText: this.translate.instant('global.myRia.jsonContent.FindLocation'),
      primaryButtonAction: (routeProvider) => routeProvider.replace(routeProvider.pages.StoresPageNew),
    });
  }

  get AustraliaManualVerificationNeededPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showRightClose: true,
      showFullPage: true,
      goToActivity: true,
      showSmallImg: true,
      img: 'assets/icons/idcard_blue_ic.svg',
      title: this.translate.instant('page.manualVerificationpage.verificationHeader'),
      msg: this.translate.instant('page.manualVerificationpage.verificationBody'),
      primaryButtonText: this.translate.instant('page.manualVerificationpage.sendDocumentsButton'),
      goEmail: true,
      emailTo: this.translate.instant('global.fraudEmail'),
      emailSubject: 'Identity documents sending',
      newFooterStyle: true,
      tips: [
        { text: 'page.manualVerificationpage.step1Body' },
        { text: 'page.manualVerificationpage.step2Body', showPin: true, copyText: this.translate.instant('global.fraudEmail') }, // pin to allow user to copy text
        { text: 'page.manualVerificationpage.step3Body' }],
      tipHeadings: [
        { text: 'page.manualVerificationpage.step1Header' },
        { text: 'page.manualVerificationpage.step2Header' },
        { text: 'page.manualVerificationpage.step3Header' }]
    });
  }

  get OnfidoServerTimeoutErrorPage(): INavInfo {
    if (this.isUSWalletVeriffFlow) {
      return this.getNavInfo('ModalAlertPage', {
        hideHeader: true,
        verticalCenter: true,
        customBodyClass: 'us-ekyc-result',
        img: 'assets/imgs/id-rejected.svg',
        customeImgStyle: { width: '120px', height: 'auto' },
        title: "We couldn't verify your identity",
        msg: 'Please check your information and try again.',
        primaryButtonText: 'Try again',
        showFullPage: true,
        newFooterStyle: true
      });
    }
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showRightClose: true,
      goToActivity: true,
      img: 'assets/imgs/Warning.svg',
      title: this.translate.instant('page.sendMoneyAdditionalDetailsPage.verificationErrorHeader'),
      msg: this.translate.instant('page.sendMoneyAdditionalDetailsPage.verificationErrorContent'),
      primaryButtonText: this.translate.instant('global.gotItButton'),
      showCircle: true,
      newFooterStyle: true
    }); 
  }

  get OnfidoConfirmCancelPage(): INavInfo {
    const bodyKey = APP_CONFIG.region.region === Region.SN
      ? 'page.ewalletpage.eKYCpendingSN.body'
      : 'page.ewalletpage.eKYCpendingMY.body';
    return this.getNavInfo('ModalAlertPage', {
        showFullPage: true,
        showRightClose: false,
        img: 'assets/icons/warning_yellow.png',
        showCircle: true,
        title: this.translate.instant('page.ewalletpage.eKYCpending.header'),
        msg: this.translate.instant(bodyKey),
        primaryButtonText: this.translate.instant('page.ewalletpage.eKYCpending.continueButton'),
        primaryButtonAction: (routeProvider: any) => {
          routeProvider.pop();
        },
        showSecondaryButton: true,
        secondaryButtonText: this.translate.instant('page.ewalletpage.eKYCpending.exitButton'),
        secondaryButtonAction: (routeProvider: any) => {
          routeProvider.setRoot(routeProvider.pages.TabsHomePage);
        },
        trackEvent: AnalyticsEventType.IdentityVerificationPendingViewed,
        trackParams: { location: 'WalletKycPending' },
        newFooterStyle: true,
      });
  }

  get EkycMethodSelectPage(): INavInfo {
    return this.getNavInfo('EkycMethodSelectPage');
  }

  get NzDocumentSelectionPage(): INavInfo {
    return this.getNavInfo('NzDocumentSelectionPage');
  }

  get NzNfcInfoPage(): INavInfo {
    return this.getNavInfo('NzNfcInfoPage');
  }

  get AddCardPage(): INavInfo {
    return this.getNavInfo('PaymentCardPage');
  }

  get PaymentCardAddAdyenPage(): INavInfo {
    return this.getNavInfo('PaymentCardAddAdyenPage');
  }

  get PaymentCardAddWorldpayPage(): INavInfo {
    return this.getNavInfo('PaymentCardAddWorldpayPage');
  }


  get CardDetailsPage(): INavInfo {
    return this.getNavInfo('PaymentCardDetailsPage');
  }

  get ReferAFriendPage(): INavInfo {
    return this.getNavInfo('ReferAFriendPage');
  }

  get ReferAFriendNewPage(): INavInfo {
    return this.getNavInfo('ReferAFriendNewPage');
  }

  get InfoReferAFriendPage(): INavInfo {
    return this.getNavInfo('RafInformationPage', {
      showClose: false,
      border: false,
      goToActivity: true,
      img: 'assets/img/refer-a-friend/refer_a_friend_gift.svg',
    });
  }


  get PromotionListPage(): INavInfo {
    return this.getNavInfo('PromotionListPage');
  }

  get IterablesInAppPage(): INavInfo {
    return this.getNavInfo('IterablesInappPage');
  }

  get IterablesInappNotificationPage(): INavInfo {
    return this.getNavInfo('IterablesInappNotificationPage');
  }

  get RegistrationStepBmInfoPage(): INavInfo {
    return this.getNavInfo('RegistrationStepBmInfoPage');
  }

  get TrackTransferInfoPage(): INavInfo {
    return this.getNavInfo('TrackTransferInfoPage');
  }

  get TrackTransferTimelinePage(): INavInfo {
    return this.getNavInfo('TrackTransferTimelinePage');
  }

  get TrackTransferDetailsPage(): INavInfo {
    return this.getNavInfo('TrackTransferDetailsPage');
  }

  get CheckPricesSelectActionsheet(): INavInfo {
    return this.getNavInfo('CheckPricesSelectActionsheet');
  }

  get VpaFormPage(): INavInfo {
    return this.getNavInfo('VpaFormPage');
  }

  get VisaUploadPage(): INavInfo {
    return this.getNavInfo('VisaUploadPage');
  }

  get MobileWalletInfoPage(): INavInfo {
    return this.getNavInfo('WalletInfoPage');
  }

  get UpdateIdOnStorePage(): INavInfo{
    return this.getNavInfo('ModalAlertPage', {
      img: 'assets/icons/new-store.svg',
      showFullPage: true,
      showHelp: true,
      showBack: true,
      imageWithCircle: true,
      showSmallImgWithTopPadding: true,
      title: this.translate.instant("page.identityVerification:Singpass.verifyRiaStore:title"),
      msg: this.translate.instant('page.identityVerification:Singpass.verifyRiaStore:content'),
      primaryButtonText: this.translate.instant('global.myRia.jsonContent.FindLocation'),
      primaryButtonAction: (routeProvider) => routeProvider.push(routeProvider.pages.FindRiaLocationsPage,{ showBack: true, hideUserIcon:true}),
    });
  }

  get WalletDepositPage(): INavInfo{
    return this.getNavInfo('WalletDepositPage');
  }
  get WalletDepositLocationGuidePage(): INavInfo{
    return this.getNavInfo('WalletDepositLocationGuidePage');
  }

  get WalletDepositJomPayPage(): INavInfo{
    return this.getNavInfo('WalletDepositJomPayPage');
  }
  get WalletWithdrawOrPaymentPage(): INavInfo{
    return this.getNavInfo('WalletWithdrawOrPaymentPage', {}, false, 'Make a withdrawal','WalletWithdrawOrPaymentPage');
  }
  get WalletWithdrawOrPaymentDetailsPage(): INavInfo{
    return this.getNavInfo('WalletWithdrawOrPaymentDetailsPage', {}, false, '', 'Withdrawal details','WalletWithdrawOrPaymentDetailsPage');
  }
  get WalletTranscationDetailPage(): INavInfo{
    return this.getNavInfo('WalletTranscationDetailPage');
  }
  get WalletWithdrawStepsPage(): INavInfo {
    return this.getNavInfo('WalletWithdrawStepsPage', {}, false, '', 'Steps to withdraw','WalletWithdrawStepsPage');
  }

  get WalletPinPage(): INavInfo {
    return this.getNavInfo('WalletPinPage', {}, false, '', 'Enter your PIN','WalletPinPage');
  }

  get WalletCommonInfoPage(): INavInfo {
    return this.getNavInfo('WalletCommonInfoPage', {}, false, '', '','WalletCommonInfoPage');
  }

  get VerifyIdentityPage(): INavInfo {
    return this.getNavInfo('VerifyIdentityPage', {}, false, '', 'Forgot PIN', "VerifyIdentityPage");
  }

  get ContactDetailsPage(): INavInfo {
    return this.getNavInfo('ContactDetailsPage', {}, false, '', '','ContactDetailsPage');
  }

  get WalletActivationSuccessPage(): INavInfo {
    return this.getNavInfo('WalletActivationSuccessPage');
  }

  get WalletActivationFailedPage(): INavInfo {
    return this.getNavInfo('WalletActivationFailedPage');
  }
  get WalletActivationFailedUnknownPageModal(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      img: 'assets/imgs/Warning.svg',
      showFullPage: true,
      hasSecondaryButton: true,
      imageWithCircle: false,
      showHelp: false,
      showBack: false,
      hideHeader:true,
      showRightClose: true,
      title: this.translate.instant("page.eWalletPage.walletErrorHeader"),
      msg:this.translate.instant('page.eWalletPage.setupErrorBody'),
      secondaryButtonText: this.translate.instant('global.contactCustomerCareButton'),
      primaryButtonText: this.translate.instant('global.tryAgainButton'),
      dismissFromSecondary: false,
      dismissFromPrimary: false
    });
  }


  get CardSettingsHomePage(): INavInfo{
    return this.getNavInfo('CardSettingsHomePage');
  }

  get CardSettingsPage(): INavInfo{
    return this.getNavInfo('CardSettingsPage');
  }

  get CardReplaceReasonsPage(): INavInfo{
    return this.getNavInfo('CardReplaceReasonsPage');
  }

  get CardLimitsAndFeesPage(): INavInfo{
    return this.getNavInfo('CardLimitsAndFeesPage');
  }

  get ActionsheetInfoPage(): INavInfo{
    return this.getNavInfo('ActionsheetInfoPage');
  }

  get SendPspChangePaymentMethod(): INavInfo{
    return this.getNavInfo('SendPspChangePaymentMethod');
  }

  get SendPspSummaryActionsheet(): INavInfo{
    return this.getNavInfo('SendPspSummaryActionsheet');
  }

  get SendPspConfirmActionsheet(): INavInfo{
    return this.getNavInfo('SendPspConfirmActionsheet');
  }

  get SendPspCvvActionsheet(): INavInfo{
    return this.getNavInfo('SendPspCvvActionsheet');
  }

  get SendPspContainerActionsheet(): INavInfo{
    return this.getNavInfo('SendPspContainerActionsheet');
  }

  get ActionsheetSelectPage(): INavInfo{
    return this.getNavInfo('ActionsheetSelectPage');
  }

  get LiteRegActionSheetPage(): INavInfo{
    return this.getNavInfo('ActionsheetInfoPage',{
      header: this.translate.instant('page.settingsPage.createProfileModalHeader'),
      texts: [
        this.translate.instant('page.settingsPage.createProfileModalBody'),
      ],
      primaryButton: this.translate.instant('page.settingsPage.createProfileModalButton'),
      secondaryButton: this.translate.instant('form.registrationForm.notNowButton'),
      isTextCenter: true
    });
  }


  get VerifyIdentitySelectionPage(): INavInfo {
    return this.getNavInfo('VerifyIdentitySelectionPage');
  }
  get VerifyIdentityInStorePage(): INavInfo {
    return this.getNavInfo('VerifyIdentityInStorePage');
  }
  get WalletBlockedPage(): INavInfo {
    return this.getNavInfo('WalletBlockedPage');
  }
  get DuplicationAccountErrorPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      hasSecondaryButton: true,
      secondaryButtonIsLink: true,
      img: 'assets/imgs/Identity.svg',
      title: this.translate.instant('page.findAccountPage.accountExistsHeader'),
      msg: this.translate.instant('page.findAccountPage.accountExistsDescription'),
      primaryButtonText: this.translate.instant('global.mainNavigation.signInButton'),
      primaryButtonAction: null,
      secondaryButtonText: this.translate.instant('global.callCustomerServiceButton'),
      secondaryButtonAction: null,
    });
  }

  get EWalletReachedDailyLimitErrorPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
        showClose: false,
        goToActivity: false,
        img: 'assets/img/wallet-info/error-img.svg',
        title: this.translate.instant('page.eWalletPage.dailyTxnLimitHeader'),
        msg: this.translate.instant('page.eWalletPage.dailyTxnLimitBody'),
        primaryButtonText: this.translate.instant('page.eWalletPage.goToWalletButton'),
        primaryButtonAction: null,
    });
  }

  get RlCreateAccountPage(): INavInfo {
    return this.getNavInfo('RlCreateAccountPage');
  }

  get RlCreatePhoneNumberPage(): INavInfo {
    return this.getNavInfo('RlCreatePhoneNumberPage');
  }

  get RlVerifyPhonePage():INavInfo{
    return this.getNavInfo('RlVerifyPhonePage');
  }

  get RlDynamicFieldsPage(): INavInfo {
    return this.getNavInfo('RlDynamicFieldsPage');
  }

  get DeleteAccountOptionsPage(): INavInfo {
    return this.getNavInfo('DeleteAccountOptionsPage');
  }

  get DeleteAccountFirstStep(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: false,
      showFullSizedButtons: true,
      img: 'assets/imgs/delete-icon.svg',
      title: this.translate.instant('page.settingsPage.deleteAccountConfirmHeader'),
      msg: this.translate.instant('page.settingsPage.closeAccountConfirmBody'),
      primaryButtonText: this.translate.instant('page.settingsPage.keepAccountOpenButton'),
      secondaryButtonText: this.translate.instant('page.settingsPage.deleteAccountText'),
      secondaryButtonColor: ColorProvider.getColorValue('--red-300'),
      hasSecondaryButton: true,
      newFooterStyle: false,
    });
  }

  get DeleteAccountSecondStep(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showBack: false,
      img: 'assets/imgs/account_deletion_confirmation_emoji.svg',
      title: this.translate.instant('page.settingsPage.accountDeletedHeader'),
      msg: this.translate.instant('page.settingsPage.accountDeletedBody'),
      primaryButtonText: this.translate.instant('page.settingsPage.logOutText'),
    });
  }

  get DeleteAccountErrorStep(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showBack: false,
      img: 'assets/imgs/exclamation_red.svg',
      title: this.translate.instant('global.generalErrorHeader'),
      msg: this.translate.instant('page.settingsPage.deleteAccountErrorBody'),
      primaryButtonText: this.translate.instant('global.tryAgainButton'),
    });
  }

  get DeleteAccountAlreadyRequest(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      img: 'assets/imgs/cancelOrder.svg',
      title: this.translate.instant('page.settingsPage.closeAccountSecondRequestHeader'),
      msg: this.translate.instant('page.settingsPage.closeAccountRequestedBody'),
      primaryButtonText: this.translate.instant('page.settingsPage.logOutText'),
      secondaryButtonText: this.translate.instant('global.dismissButton'),
      hasSecondaryButton: true
    })
  }

  get AccountSuccessfullyDeletedPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      img: 'assets/imgs/account_deletion_successful.svg',
      title: this.translate.instant('page.settingsPage.accountDeletedTitle'),
      msg: this.translate.instant('page.deletedAccountPage.accountDeletedBody'),
      primaryButtonText: this.translate.instant('page.registrationPage.createNewAccount.button'),
      secondaryButtonText: this.translate.instant('page.settingsPage.logOutText'),
      hasSecondaryButton: true
    })
  }

  get AccountSuccessfullyDisabledPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      img: 'assets/imgs/account_deletion_successful.svg',
      title: this.translate.instant('page.deletedAccountPage.accountDeletedTitle'),
      msg: this.translate.instant('page.deletedAccountPage.accountDisabledBody'),
      primaryButtonText: this.translate.instant('page.settingsPage.logOutText'),
      hasSecondaryButton: false
    })
  }

  get AccountUnableToDeletePendingPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      img: 'assets/imgs/exclamation_red.svg',
      title: this.translate.instant('page.settingsPage.deleteAccountErrorHeader'),
      msg: this.translate.instant('page.settingsPage.deleteAccountErrorUnpaidBody'),
      primaryButtonText: this.translate.instant('global.tryAgainButton'),
      hasSecondaryButton: false
    })
  }

  get AccountUnableToDeleteErrorPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      img: 'assets/imgs/exclamation_red.svg',
      title: this.translate.instant('page.settingsPage.deleteAccountErrorHeader'),
      msg: this.translate.instant('page.settingsPage.deleteAccountErrorBody'),
      primaryButtonText: this.translate.instant('global.tryAgainButton'),
      hasSecondaryButton: false
    })
  }

  get sendToUploadDocumentRequestPage(): INavInfo{
    return this.getNavInfo('SendFailedPage', {
      missing: MissingTransactionInfoType.RequiresAdditionalDocuments,
      errorTitle: this.translate.instant('page.transferDetailsPage.transferOnHoldTitle'),
      errorDetails: this.translate.instant('page.transferDetailsPage.transferOnHoldContent') ,
      buttonLeft: this.translate.instant('page.transferDetailsPage.uploadDocsButton'),
      buttonRight: this.translate.instant('page.transferDetailsPage.doItLaterButton'),
      newDesign: true,
    });
  }

  get AccountBlockedPage(): INavInfo{
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      img: 'assets/imgs/Warning.svg',
      title: this.translate.instant('page.accountLockedPage.headerText'),
      msg: this.translate.instant('page.accountLockedPage.bodyText'),
      primaryButtonText: this.translate.instant('page.signInFailedPage.genericGetHelpButton'),
      showCircle: false,
      newFooterStyle: true,
      newDesign: true,
      showRightClose: true,
    });
  }

  get WalletBlackListedPage(): INavInfo{
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      img: 'assets/imgs/wrong.svg',
      title: this.translate.instant('page.eWalletPage.walletTempLockHeader'),
      msg: this.translate.instant('page.eWalletPage.walletTempLockBody'),
      primaryButtonText: this.translate.instant('global.contactCustomerCareButton'),
      dismissFromPrimary: false,
      showCircle: false,
      newFooterStyle: true,
      newDesign: true,
      showRightClose: false,
      customSubTitleClass: 'custom-modal-subtitle-style'
    });
  }

  get WalletTempBlockedAndPinDisabledPage(): INavInfo{
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      img: 'assets/imgs/wrong.svg',
      title: this.translate.instant('page.eWalletPage.walletTempLockHeader'),
      msg: this.translate.instant('page.eWalletPage.incorrectPasscodeBody'),
      primaryButtonText: this.translate.instant('page.eWalletPage.forgotPINButton'),
      dismissFromPrimary: false,
      showCircle: false,
      newFooterStyle: true,
      newDesign: true,
      showRightClose: false,
      customSubTitleClass: 'custom-modal-subtitle-style'
    });
  }

  get TransferHistoryFoundMessagePage(): INavInfo{
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      img: 'assets/imgs/success.svg',
      title: this.translate.instant('page.knowledgeCheckPage.transferHistoryFoundHeader'),
      msg: this.translate.instant('page.knowledgeCheckPage.transferHistoryFoundContent'),
      primaryButtonText: this.translate.instant('global.gotItButton'),
      showCircle: false,
      newFooterStyle: true,
      newDesign: true,
      showRightClose: false,
    });
  }

  get PreviousActivityFoundMessagePage(): INavInfo{
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      img: 'assets/imgs/success.svg',
      title: this.translate.instant('page.knowledgeCheckPage.activityFoundHeader'),
      msg: this.translate.instant('page.knowledgeCheckPage.activityFoundContent'),
      primaryButtonText: this.translate.instant('global.gotItButton'),
      showCircle: false,
      newFooterStyle: true,
      newDesign: true,
      showRightClose: false,
      trackEvent: AnalyticsEventType.AccountLinkingCompletedclientside,
      trackParams: {
        country: APP_CONFIG.region.defaultCountryFrom,
        location: SegmentLocationNames.Registration
      }
    });
  }

  get CustomerFoundMergeFailedPage(): INavInfo{
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      img: 'assets/imgs/Warning.svg',
      title: this.translate.instant('page.knowledgeCheckPage.accountNotFoundHeader'),
      msg: this.translate.instant('page.knowledgeCheckPage.accountNotFoundContent'),
      primaryButtonText: this.translate.instant('global.gotItButton'),
      showCircle: false,
      newFooterStyle: true,
      newDesign: true,
      showRightClose: false,
      trackEvent: AnalyticsEventType.AccountLinkingFailedClientSide,
      trackParams: {
        country: APP_CONFIG.region.defaultCountryFrom,
        errorSubType: "",
        location: SegmentLocationNames.Registration
      }
    });
  }
  
  get RestrictedTransfers(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showBack: false,
      showFullPage: true,
      showSecondaryButton: true,
      image: 'assets/imgs/error_logo_carousel.svg',
      customBodyClass: 'restricted-transfers-modal',
      title: this.translate.instant("Page.accountLocked.transferRestrictedHeader"),
      msg: this.translate.instant("Page.accountLocked.transferRestrictedDescription"),
      primaryButtonText: this.translate.instant("Page.accountLocked.renableTransfersButton"),
      secondaryButtonText: this.translate.instant('page.hubPage.activityButton'),
    });
  }

  get DocUploadSelectPage(): INavInfo {
    return this.getNavInfo('DocUploadSelectPage');
  }

  get DocUploadMYNonNationalPage(): INavInfo {
    return this.getNavInfo('DocUploadMYNonNationalPage');
  }

  get ReEnableTransfersPage(): INavInfo {
    return this.getNavInfo('ReEnableTransfersPage');
  }

  get DocUploadPage(): INavInfo {
    return this.getNavInfo('DocUploadPage');
  }

  get SendChangeAmountPage(): INavInfo {
    return this.getNavInfo('SendChangeAmountPage');
  }
  get TabMenuPage():INavInfo {
    return this.getNavInfo('TabMenuPage');
  }
  get WalletActionsMorePage():INavInfo {
    return this.getNavInfo('WalletActionsMorePage');
  }

  get BankAccountVerifyToWalletPage() : INavInfo {
    return this.getNavInfo('BankAccountVerifyToWalletPage');
  }

  get WalletSettingsPage(): INavInfo {
    return this.getNavInfo('WalletSettingsPage');
  }

  get OnDeleteWalletFailed(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showRightClose: false,
      showBack:false,
      hideHeader:true,
      img: 'assets/imgs/Warning.svg',
      title: this.translate.instant('page.settingsPage.walletErrorHeader'),
      msg: this.translate.instant('page.settingsPage.deleteWalletErrorBody'),
      contactCTAReason: ContactModalReasons.DeleteWalletNotAvailable,
      openChatWidgetIfAvailable: true,
      secondaryButtonText : this.translate.instant('global.callCustomerServiceButton'),
      hasSecondaryButton: true,
      primaryButtonText:this.translate.instant('global.tryAgainButton')
    });
  }

  get OnDeleteWalletSuccess(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showRightClose: false,
      showClose:false,
      showBack:false,
      hideHeader:true,
      img: 'assets/imgs/welcome.svg',
      title: this.translate.instant('page.settingsPage.deleteWalletConfirmHeader'),
      msg: this.translate.instant('page.settingsPage.deleteWalletConfirmBody'),
      hasSecondaryButton: false,
      primaryButtonText:this.translate.instant('global.gotItButton'),
    });
  }

  get WalletToBankAccountsListPage(): INavInfo {
    return this.getNavInfo('WalletToBankAccountsListPage');
  }

  get LanguageSelectionPage(): INavInfo {
    return this.getNavInfo('LanguageSelectionPage');
  }

  get TopBanksPage(): INavInfo {
    return this.getNavInfo('TopBanksPage');
  }

  get CustomerFeedbackPage(): INavInfo {
    return this.getNavInfo('CustomerFeedbackPage');
  }

  get SendContinuePage(): INavInfo {
    return this.getNavInfo('SendContinuePage')
  }

  get ShowDetailsPage(): INavInfo {
    return this.getNavInfo('ShowDetailsPage');
  }


  get ConfirmEmailKycPage(): INavInfo {
    return this.getNavInfo('ConfirmEmailKycPage');
  }

  get LogoutWarning() : INavInfo {
    let timeout = null;

    return this.getNavInfo('ModalAlertPage', {
      title: this.translate.instant('page.errorMessage.sessionInactivityWarningHeader'),
      primaryButtonText: this.translate.instant('page.errorMessage.sessionInactivityWarningActionButton'),
      showFullPage: false,
      customBodyClass: "custom-modal-style",
      onInit: (page) => {
        let start = Date.now();
        const setText = () => {
          const passedSeconds = Math.floor((Date.now() - start)/1000);
          const leftSeconds = Math.max(0, APP_CONFIG.timeoutTimer - APP_CONFIG.timeoutTimerWarning - passedSeconds);
          page.subtitle = [ this.translate.instant('page.errorMessage.sessionInactivityWarningContent', { timeoutCountdown: leftSeconds }) ];
          if (leftSeconds <= 0)
            page.dismissModalAlert({ loggedOut: true });
          else {
            timeout = window.setTimeout(() => {
              setText();
            }, 500);
          }
        };
        setText();
      },
      onClose: () => {
        if (timeout)
          window.clearTimeout(timeout);
      }
    });
  }

  get ResigninPage(): INavInfo{
    return this.getNavInfo('ResigninPage');
  }

  get BiometricEnablePage(): INavInfo{
    return this.getNavInfo('BiometricEnablePage');
  }

  get CardApplyProcess(): INavInfo {
    return this.getNavInfo('CardModalAlertPage', {
      isWaiting: false,
      imgSrc: "../assets/img/wallet/ria-prepaid-card-physical.svg",
      title: this.translate.instant('page.eWalletPage.prepaidCardIssuancePage.getCardTitle'),
      subTitle: this.translate.instant('page.eWalletPage.prepaidCardIssuancePage.getCardText'),
      isSubtitleInnerHTML: true,
      showPrimaryButton: true ,
      primaryBtnText: this.translate.instant('page.eWalletPage.prepaidCardIssuancePage.wantButton'),
      showSecondaryButton: true,
      secondaryBtnText: this.translate.instant('global.notNowButton'),
      primaryButtonAction: (routeProvider) => {
        routeProvider.openModal(routeProvider.pages.CardSelectionPage, { isShowBack: true })
      }
    });
  }

  get CardModalAlertPage(): INavInfo {
    return this.getNavInfo('CardModalAlertPage');
  }

  get ApplyPhysicalCardDetailPage(): INavInfo {
    return this.getNavInfo('ApplyPhysicalCardDetailPage');
  }

  get CardSelectionPage(): INavInfo {
    return this.getNavInfo('CardSelectionPage');
  }

  get PhysicalCardSelectPage(): INavInfo {
    return this.getNavInfo('PhysicalCardSelectPage');
  }

  get CardPinSetupPage(): INavInfo {
    return this.getNavInfo('CardPinSetupPage');
  }


  get getCardToNearStore(): INavInfo {
    return this.getNavInfo('CardModalAlertPage', {
      isWaiting: false,
      showBack: false,
      isCircleImg: true,
      imgSrc: "../assets/imgs/wallet-store-icons.png",
      title: this.translate.instant('page.eWalletPage.prepaidCardIssuancePage.inPersonTitle'),
      subTitle: this.translate.instant('page.eWalletPage.prepaidCardIssuancePage.inPersonText'),
      showPrimaryButton: true ,
      primaryBtnText:this.translate.instant('global.findRiaStoreButton'),
      showSecondaryButton: false,
      primaryButtonAction: (routeProvider) => {
        routeProvider.openModal(routeProvider.pages.StoresPageNew, { isSetCloseToHomePage: true}, null, 'store-locator-modal')
      }
    });
  }

  get getCardToBackNearStore(): INavInfo {
    return this.getNavInfo('CardModalAlertPage', {
      isWaiting: false,
      showBack: true,
      isCircleImg: true,
      imgSrc: "../assets/imgs/wallet-store-icons.png",
      title: this.translate.instant('page.eWalletPage.prepaidCardIssuancePage.inPersonTitle'),
      subTitle: this.translate.instant('page.eWalletPage.prepaidCardIssuancePage.inPersonText'),
      showPrimaryButton: true ,
      primaryBtnText:this.translate.instant('global.findRiaStoreButton'),
      showSecondaryButton: false,
    });
  }

  get ListActionsheetPage(): INavInfo{
    return this.getNavInfo('ListActionsheetPage');
  }

  get EmailInfoPage() :INavInfo{
    return this.getNavInfo('ModalAlertPage', {
      showRightClose: true,
      showBack: false,
      closeLicensePage: true,
      img: 'assets/imgs/info_logo.svg',
      title: this.translate.instant('page.checkEmailPage.checkEmailTitle'),
      msg: this.translate.instant('page.checkEmailPage.checkEmailText'),
      primaryButtonText: this.translate.instant('global.okButton'),
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant('page.sendMoneyPaymentPage.payInStoreButton'),
      showFullPage: true,
    });
  }

  get ReplaceCardConfirmationModal() :INavInfo{
    return this.getNavInfo('ModalAlertPage', {
      title: this.translate.instant('page.eWalletPage.prepaidCardActivityPage.replaceCardTitle'),
      subtitle: this.translate.instant('page.eWalletPage.prepaidCardActivityPage.cardBlockedText'),
      primaryButtonText: this.translate.instant('page.eWalletPage.prepaidCardActivityPage.cardReplacementButton'),
      secondaryButtonText: this.translate.instant('global.genericCancelButton'),
      image: 'assets/icons/card-replace-reasons.svg',
      showSecondaryButton : true,
      swapButtons: true,
      dismissFromPrimary: true,
    });
  }

  get ReplaceCardConfirmationForDamagedModal() :INavInfo{
    return this.getNavInfo('ModalAlertPage', {
      title: this.translate.instant('page.eWalletPage.prepaidCardActivityPage.replaceCardTitle'),
      subtitle: this.translate.instant('page.eWalletPage.prepaidCardActivityPage.cardReplacementText'),
      primaryButtonText: this.translate.instant('global.callCustomerServiceButton'),
      secondaryButtonText: this.translate.instant('global.notNowButton'),
      image: 'assets/icons/card-replace-reasons.svg',
      showSecondaryButton : true,
      swapButtons: true,
      dismissFromPrimary: true,
    });
  }

  get PhysicalCardPinSetWarningModal() :INavInfo{
    return this.getNavInfo('CardModalAlertPage', {
      isWaiting: false,
      isCircleImg: true,
      imgSrc: "assets/icon/yellow-warning-icon.svg",
      title: this.translate.instant('global.generalErrorHeader'),
      showPrimaryButton: true,
      primaryBtnText: this.translate.instant('global.tryAgainButton'),
      showSecondaryButton: true,
      secondaryBtnText: this.translate.instant('global.notNowButton'),
    });
  }

  get PhysicalCardPinSetSuccessModal() :INavInfo{
    return this.getNavInfo('CardModalAlertPage', {
      isWaiting: false,
      isCircleImg: false,
      imgSrc: "../assets/img/wallet/ria-prepaid-card-physical.svg",
      title: this.translate.instant('page.eWalletPage.prepaidCardPINPage.cardActivatedTitle'),
      subTitle: this.translate.instant('page.eWalletPage.prepaidCardPINPage.cardActivatedText'),
      showPrimaryButton: true,
      primaryBtnText: this.translate.instant('global.okButton'),
      showSecondaryButton: false,
    });
  }
  get PhysicalCardPinChangeSuccessModal() :INavInfo{// created diffrent func as get not able to add param to it
    return this.getNavInfo('CardModalAlertPage', {
      isWaiting: false,
      isCircleImg: false,
      imgSrc: "../assets/img/wallet/ria-prepaid-card-physical.svg",
      title: this.translate.instant('page.eWalletPage.prepaidCardPINPage.cardActivatedTitle'),
      subTitle: "",
      showPrimaryButton: true,
      primaryBtnText: this.translate.instant('global.okButton'),
      showSecondaryButton: false,
    });
  }

  get CardCvvSetUpPage() :INavInfo{
    return this.getNavInfo('CardPinSetupPage', {
      title: this.translate.instant('page.eWalletPage.physicalCardActivationPage.activateCardTitle'),
      subTitle: this.translate.instant('page.eWalletPage.physicalCardActivationPage.securityCodeText'),
      isShowBack: true,
      showPrimaryButton: true,
      isCvvCheck: true,
      isPINCheck: false,
      isPinConfirmCheck: false,
    });
  }

  get CardCvvSetUpWarningModal() :INavInfo{
    return this.getNavInfo('CardModalAlertPage', {
      isWaiting: false,
      isCircleImg: true,
      imgSrc: "assets/icon/yellow-warning-icon.svg",
      title: this.translate.instant('page.eWalletPage.physicalCardActivationPage.incorrectCVVTitle'),
      showPrimaryButton: true,
      secondaryBtnText: this.translate.instant('global.notNowButton'),
    });
  }

  get CardDidnotArriveModalPage() :INavInfo{
    return this.getNavInfo('ModalAlertPage', {
      title: this.translate.instant('page.eWalletPage.physicalCardActivationPage.cardHasNotArrivedTitle'),
      subtitle: this.translate.instant('page.eWalletPage.physicalCardActivationPage.cardHasNotArrivedText'),
      primaryButtonText: this.translate.instant('global.callCustomerServiceButton'),
      showPrimaryButton: true,
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant('global.notNowButton'),
      secondaryAction: null,
      dismissFromSecondary: true,
      openChatWidgetIfAvailable: true,
    });
  }

  get PhysicalCardActivePage() :INavInfo{
    return this.getNavInfo('CardModalAlertPage',{
      isWaiting: false,
      imgSrc: "../assets/img/wallet/ria-prepaid-card-physical.svg",
      title: this.translate.instant('page.eWalletPage.physicalCardActivationPage.receivedCardTitle'),
      subTitle: this.translate.instant('page.eWalletPage.physicalCardActivationPage.receivedCardText'),
      showPrimaryButton: true,
      primaryBtnText: this.translate.instant('page.sendMoneyAdditionalDetailsPage.relationshipPoliticalPosition.yes'),
      showSecondaryButton: true,
      imgWidth: "250px",
      imgHeight: "250px",
      secondaryBtnText: this.translate.instant('page.eWalletPage.physicalCardActivationPage.receivedCardButton'),
    })
  }

  get PhysicalCardApplyWarningModal() :INavInfo{
    return this.getNavInfo('CardModalAlertPage',{
      isWaiting: false,
      isCircleImg: true,
      imgSrc: "assets/icon/yellow-warning-icon.svg",
      title: this.translate.instant('global.errorGeneralHeader'),
      subTitle: this.translate.instant('page.eWalletPage.prepaidCardIssuancePage.errorText'),
      isSubtitleInnerHTML: true,
      showSecondaryButton: true,
      secondaryBtnText: this.translate.instant('global.tryAgainButton'),
      showPrimaryButton: true,
      primaryBtnText: this.translate.instant('global.findRiaStoreButton'),
    })
  }

  get VirtualCardApplyWarningModal() :INavInfo{
    return this.getNavInfo('CardModalAlertPage',{
      isWaiting: false,
      isCircleImg: true,
      imgSrc: "assets/icon/yellow-warning-icon.svg",
      title: this.translate.instant('global.errorGeneralHeader'),
      subTitle: this.translate.instant('page.eWalletPage.prepaidCardIssuancePage.errorText'),
      isSubtitleInnerHTML: true,
      showSecondaryButton: true,
      secondaryBtnText: this.translate.instant('global.tryAgainButton'),
      showPrimaryButton: false,
    })
  }

  get VirtualCardContinuePage() :INavInfo{
    return this.getNavInfo('CardModalAlertPage',{
      msg: this.translate.instant('page.eWalletPage.prepaidCardIssuancePage.creatingVirtualCard'),
      isWaiting: true,
    })
  }

  get VirtualCardApplyModal() :INavInfo{
    return this.getNavInfo('ModalAlertPage',{
      image: "assets/imgs/wallet-virtual-icon.png",
      imageWithCircle: true,
      title: this.translate.instant('page.eWalletPage.prepaidCardIssuancePage.getVirtualCard'),
      primaryButtonText: this.translate.instant('global.agreeAndContinueButton'),
      showPrimaryButton: true,
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant('global.notNowButton'),
      secondaryAction: null,
      dismissFromSecondary: true,
    })
  }

  get CardPinSetUpConfirmPinsetUpPage() :INavInfo{
    return this.getNavInfo('CardPinSetupPage',{
      isShowBack: true,
      showPrimaryButton: false,
      isCvvCheck: false,
    })
  }

  get VirtualCardApplySuccessModal() :INavInfo{
    return this.getNavInfo('CardModalAlertPage',{
      isWaiting: false,
      imgSrc: "../assets/img/wallet/ria-prepaid-card-virtual.svg",
      title: this.translate.instant('page.eWalletPage.prepaidCardIssuancePage.virtualCardReadyTitle'),
      subTitle: this.translate.instant('page.eWalletPage.prepaidCardIssuancePage.virtualCardReadyText'),
      showPrimaryButton: true,
      primaryBtnText: this.translate.instant('page.eWalletPage.prepaidCardIssuancePage.viewVirtualCardButton'),
      showSecondaryButton: true ,
      secondaryBtnText: this.translate.instant('global.notNowButton'),
    })
  }

  get PhysicalCardApplySuccessModal() :INavInfo{
    return this.getNavInfo('CardModalAlertPage',{
      isWaiting: false,
      imgSrc: "../assets/img/wallet/ria-prepaid-card-physical.svg",
      title: this.translate.instant('page.eWalletPage.prepaidCardIssuancePage.cardRequestSuccessTitle'),
      subTitle: this.translate.instant('page.eWalletPage.prepaidCardIssuancePage.cardRequestSuccessText'),
      showPrimaryButton: true,
      primaryBtnText: this.translate.instant('global.gotItButton'),
      showSecondaryButton: false,
    })
  }
  get WalletAllTransactionsPage(): INavInfo{
    return this.getNavInfo('WalletAllTransactionsPage');
  }

  get LoadingWithActionPage(): INavInfo{
    return this.getNavInfo("LoadingWithActionPage");
  }


  get AgentsSearchPage(): INavInfo{
    return this.getNavInfo('AgentsSearchPage')
  }

  get MobileTopUpNumberPage(): INavInfo {
    return this.getNavInfo('MobileTopUpNumberPage')
  }

  get MobileTopUpPlanPage(): INavInfo {
    return this.getNavInfo('MobileTopUpPlanPage', {
    })
  }

  get MobileProviderPage(): INavInfo {
    return this.getNavInfo('MobileProviderPage', {
    })
  }

  get MobileTopUpImportContactPage(): INavInfo {
    return this.getNavInfo('MobileTopUpImportContactPage', {
    })
  }

  get MobileTopUpSummaryPage(): INavInfo {
    return this.getNavInfo('MobileTopUpSummaryPage', {
    })
  }

  get mobileTopUpErrModalePage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      img: 'assets/icon/yellow-warning-icon.svg',
      showFullPage: false,
      hasSecondaryButton: true,
      imageWithCircle: true,
      title: this.translate.instant("global.errorGeneralHeader"),
      secondaryButtonText: this.translate.instant('global.genericCancelButton'),
      primaryButtonText: this.translate.instant('global.tryLaterButton'),
      dismissFromSecondary: true,
      dismissFromPrimary: true
    });
  }

  get MobileTopUpProcessPage(): INavInfo {
    return this.getNavInfo('MobileTopUpProcessPage', {
    })
  }

  get mobileTopUpFaileModalePage(): INavInfo {
    let secondaryButtonText = "global.gotItButton";
    if(APP_CONFIG.region.region === Region.SN){
      secondaryButtonText = 'page.billPayPage.backToWalletHomeButton';
    }
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showRightClose: false,
      showHelp: false,
      image: "assets/icons/error-cancel-icon.svg",
      imageWithCircle: true,
      title: this.translate.instant('page.errorMessage.ria_errormessage_pindebitnonsufficientfunds'),
      subtitle: this.translate.instant('page.topUpPage.paymentErrorBody'),
      primaryButtonText: this.translate.instant('global.tryAgainButton'),
      showPrimaryButton: true,
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant(secondaryButtonText)
    });
  }

  get mobileTopUpSuccessModalePage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showRightClose: false,
      image: "assets/icons/checkmark_green.svg",
      imageWithCircle: true,
      primaryButtonText: this.translate.instant('global.gotItButton'),
      showPrimaryButton: true,
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant('page.activityPage.shareButton')
    });
  }

  get ComplianceQuestionsInfoPage(): INavInfo {
    return this.getNavInfo('ComplianceQuestionsInfoPage');
  }

  get QrScannerHelperPage(): INavInfo {
    return this.getNavInfo('QrScannerHelperPage');
  }

  get ReferralRedeemPage(): INavInfo {
    return this.getNavInfo('ReferralRedeemPage');
  }

  get WalletCheckLimitsPage(): INavInfo {
    return this.getNavInfo('WalletCheckLimitsPage');
  }

  get WalletReceiveMoneyTrackPage(): INavInfo {
    return this.getNavInfo('WalletReceiveMoneyTrackPage');
  }

  get WalletReceiveMoneyTrackDetailPage(): INavInfo {
    return this.getNavInfo('WalletReceiveMoneyTrackDetailPage');
  }

  get FailedWalletDepostiInwardRemittance(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showRightClose: false,
      showHelp: false,
      imageWithCircle: true,
      image: "assets/icons/error-cancel-icon.svg",
      title: this.translate.instant('page.errorMessage.ria_errormessage_pindebitnonsufficientfunds'),
      subtitle: this.translate.instant('page.topUpPage.paymentErrorBody'),
      primaryButtonText: this.translate.instant('global.tryAgainButton'),
      showPrimaryButton: true,
      showSecondaryButton: true,
      secondaryButtonText: this.translate.instant('page.billPayPage.backToWalletHomeButton')
    });
  }

  get WalletReceiveMoneyProcessPage(): INavInfo {
    return this.getNavInfo('WalletReceiveMoneyProcessPage');
  }

  get WalletBillersPage(): INavInfo {
    return this.getNavInfo('WalletBillersPage');
  }

  get WalletBillPayPage(): INavInfo {
    return this.getNavInfo('WalletBillPayPage');
  }

  get WalletBillpaySummaryPage(): INavInfo {
    return this.getNavInfo('WalletBillpaySummaryPage');
  }

  get WalletPinForgotPasscodeModalPage(): INavInfo {
    return this.getNavInfo('WalletPinForgotPasscodeModalPage',{
      isSuccessPage: false
    });
  }

  get WalletPinForgotVerificationPage(): INavInfo {
    return this.getNavInfo('WalletPinForgotVerificationPage');
  }

  get WalletPinForgotPasscodeSuccessPage(): INavInfo {
    return this.getNavInfo('WalletPinForgotPasscodeModalPage',{
      isSuccessPage: true
    })
  }
  get SingpassProfileReviewDetailsPage(): INavInfo {
    return this.getNavInfo('SingpassProfileReviewDetailsPage');
  }

  get SingpassProfileVerifyMethodSelectPage(): INavInfo {
    return this.getNavInfo('ProfileVerifyMethodSelectPage');
  }

  get SingpassVerifyUserSuccessPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      img: 'assets/icons/checkmark_green.svg',
      showHelp: false,
      hideHeader: true,
      showBack: false,
      showImage: true,
      imageWithCircle: true,
      showFullPage: true,
      showSmallImgWithTopPadding: true,
    });
  }

  get SingpassVerificationFailedPage(): INavInfo{
    return this.getNavInfo('SingpassVerificationFailedPage');
  }

  get SingpassReviewTransferPayAtStore(): INavInfo{
    return this.getNavInfo('ModalAlertPage', {
      title: this.translate.instant("page.identityVerification:Singpass.verificationTroublePayInStore:title"),
      image: 'assets/icons/warning-round.svg',
      subtitle: this.translate.instant("page.identityVerification:Singpass.verificationTroublePayInStore:content"),
      primaryButtonText: this.translate.instant('global.genericContinueButton'),
      secondaryButtonText: this.translate.instant('global.genericCancelButton'),
      showSecondaryButton: true,
    });
  }

  get SingpassGenericErrorModal(): INavInfo{
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showRightClose: true,
      showHelp: false,
      showBack: false,
      imageWithCircle: true,
      showSmallImgWithTopPadding: true,
      title: this.translate.instant('page.identityVerification:Singpass.SingpassStartTrouble:title'),
      image: 'assets/icon/exclamation.svg',
      subtitle: this.translate.instant('page.identityVerification:Singpass.SingpassStartTrouble:content'),
      primaryButtonText: this.translate.instant('page.identityVerification:Singpass.SingpassStartTrouble:button'),
    });
  }

  get WalletBalanceInfoPage(): INavInfo {
    return this.getNavInfo('WalletBalanceInfoPage')
  }

  get OtpScreenPage(): INavInfo {
    return this.getNavInfo('OtpScreenPage');
  }

  get verifyYourIdentityAtRiaStore(): INavInfo{
    return this.getNavInfo('ModalAlertPage', {
      title: this.translate.instant("page.identityVerification.regulatorySandbox.verifyRiaStore.title"),
      image: 'assets/imgs/ria_store.svg',
      subtitle: this.translate.instant("page.identityVerification.regulatorySandbox.verifyRiaStore.content"),
      primaryButtonText: this.translate.instant('global.myRia.jsonContent.CheckLocations'),
      showFullPage: true,
      showImage: true,
      showBack: true,
      showHelp: false,
      primaryButtonAction: (routeProvider) => routeProvider.replace(routeProvider.pages.StoresPageNew),
    });
  }

  get SandboxDocumentSelectionPage(): INavInfo {
    return this.getNavInfo('SandboxDocumentSelectionPage');
  }

  get SandboxBankValidationPage(): INavInfo {
    return this.getNavInfo('SandboxBankValidationPage');
  }

  get SandboxUploadVisaAlertPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      img: 'assets/imgs/visa-passport.png',
      showHelp: true,
      showBack: false,
      showImage: true,
      imageWithCircle: true,
      showFullPage: true,
      title: this.translate.instant("page.visaUploadNotice.header"),
      msg: this.translate.instant("page.visaUploadNotice.content"),
      primaryButtonText: this.translate.instant('page.visaUploadNotice.button.uploadNow'),
      secondaryButtonText: this.translate.instant('page.visaUploadNotice.button.uploadLater'),
      hasSecondaryButton: true,
      primaryButtonAction: (routeProvider) => {
        routeProvider.push(routeProvider.pages.DocUploadPage, {
          docType: DocType.Visa,
          isFromOnfidoEkyc: true,
          // TODO: Add props to integrate new EndPoint
        });

      }
    });
  }

  get SandboxVisaUnderReviewPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      img: 'assets/imgs/visa-review-hourglass.png',
      showHelp: true,
      showBack: false,
      showImage: true,
      imageWithCircle: true,
      showFullPage: true,
      title: this.translate.instant("page.visaUploadSuccess.header"),
      msg: this.translate.instant("page.visaUploadSuccess.content"),
      primaryButtonText: this.translate.instant('global.gotItButton'),
      primaryButtonAction: (routeProvider) => {
        routeProvider.setRoot(routeProvider.pages.TabsHomePage);
      }
    });
  }

  get RiaWalletInvitePage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: false,
      showFullSizedButtons: true,
      title: this.translate.instant('page.sendMoneyPaymentPage.eWalletConfirmNumberHeader'),
      msg: this.translate.instant('page.sendMoneyPaymentPage.eWalletConfirmNumberBody'),
      primaryButtonText: this.translate.instant('global.confirmButton'),
      secondaryButtonText: this.translate.instant('global.genericCancelButton'),
      hasSecondaryButton: true,
      newFooterStyle: false,
    });
  }

  get CDMDepositPage(): INavInfo {
    return this.getNavInfo('CDMDepositPage');
  }

  get CDMBankSelectionPage(): INavInfo {
    return this.getNavInfo('CDMBankSelectionPage');
  }


  get EkycRequiredPage(): INavInfo {
    return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showFullSizedButtons: true,
      verticalCenter: true,
      img: 'assets/icons/hourglass.svg',
      title: this.translate.instant('page.identityVerificationPage.verificationRequiredHeader'),
      msg: this.translate.instant('page.identityVerificationPage.verificationRequiredBody'),
      primaryButtonText: this.translate.instant('page.bankVerificationPage.verifyNowButton'),
      secondaryButtonText: this.translate.instant('page.paymentAccountVerifyCheckPage.doLaterButton'),
      hasSecondaryButton: true,
      newFooterStyle: false,
    });
  }

  get EkycExpiredPage(): INavInfo {
      return this.getNavInfo('ModalAlertPage', {
      showFullPage: true,
      showFullSizedButtons: true,
      verticalCenter: true,
      img: 'assets/icons/expired-id-icon.png',
      title: this.translate.instant('page.sendFailedPage.iDExpiredHeader'),
      msg: this.translate.instant('page.identityVerificationPage.verificationRequiredBody'),
      primaryButtonText: this.translate.instant('page.bankVerificationPage.verifyNowButton'),
      secondaryButtonText: this.translate.instant('page.paymentAccountVerifyCheckPage.doLaterButton'),
      hasSecondaryButton: true,
      newFooterStyle: false,
      imageWithCircle: true,
      imageWithCirclePadding: '10px'
    });
  }
  
    get IncomingTransactionPage(): INavInfo {
      return this.getNavInfo('ModalAlertPage', {
        img: 'assets/imgs/visa-review-hourglass.png',
        verticalCenter: true,
        title: this.translate.instant('page.eWalletPage.incomingPayment.Title'),
        msg: this.translate.instant("page.eWalletPage.incomingPayment.Body"),
        primaryButtonText: this.translate.instant('global.genericContinueButton'),
        primaryButtonAction: (routeProv) => routeProv.setRoot(routeProv.pages.TabsHomePage),
        showSecondaryButton: false,
        showFullPage: true,
        isModal: true,
        showBack: true,
        biggerImgClass: true,
    });
  }  

    get PendingTransactionPage(): INavInfo {
      return this.getNavInfo('ModalAlertPage', {
        image: "assets/imgs/cash-icon.png",
        verticalCenter: true,
        msg: this.translate.instant(
          "page.walletAcceptTransferPage.acceptTransferBody"
        ),
        primaryButtonText: this.translate.instant(
          "page.sendMoneyAdditionalDetailsPage.additionalDetailsIdentityVerificationActionButton"
        ),
        secondaryButtonText: this.translate.instant("global.notNowButton"),
        showSecondaryButton: true,
        showFullPage: true,
        isModal: true,
        showBack: true,
        biggerImgClass: true,
      });
  }  



      get ActiveWalletPasscodeUpdatedPage(): INavInfo {
        return this.getNavInfo('ModalAlertPage', {
          image: "assets/icons/checkmark_green.svg",
          verticalCenter: true,
          title: this.translate.instant('page.eWalletPage.changePasscodeSuccess'),
          msg: this.translate.instant('page.eWalletPage.passcodeCreationSuccess.Body'),
          primaryButtonText: this.translate.instant('global.genericContinueButton'),
          primaryButtonAction: (routeProv) => routeProv.setRoot(routeProv.pages.TabsHomePage),
          showSecondaryButton: false,
          showFullPage: true,
          showBack: false,
          imageWithCircle: true,
          imageWithCirclePadding: '10px',
        });
    } 

      get FetchContactDetailsLimitReachedPage(): INavInfo {
        return this.getNavInfo('ModalAlertPage', {
          img: 'assets/imgs/warning_triangle.png',
          imageWithCircle: true,
          showFullPage: false,
          showFullSizedButtons: true,
          title: this.translate.instant('global.errorGeneralHeader'),
          msg: this.translate.instant('page.eWalletPage.maxAttemptsError.Body'),
          primaryButtonText: this.translate.instant('global.okButton'),
          hasSecondaryButton: false,
          newFooterStyle: false,
        });
      }

      get WalletScannerAccessModal(): INavInfo {
        return this.getNavInfo('ModalAlertPage', {
          showFullPage: false,
          title: this.translate.instant('page.eWalletPage.scannerAccessModal.header'),
          msg: this.translate.instant('page.eWalletPage.scannerAccessModal.body'),
          primaryButtonText: this.translate.instant('page.eWalletPage.scannerAccessModal.paymentButton'),
          showSecondaryButton: true,
          secondaryButtonText: this.translate.instant('page.eWalletPage.scannerAccessModal.withdrawButton'),
          secondaryColor: 'grey',
          showTertiaryButton: true,
          tertiaryButtonText: this.translate.instant('page.eWalletPage.scannerAccessModal.riaQRButton'),
          tertiaryColor: 'secondary',
        });
      }

}



// WEBPACK FOOTER //
// ./src/providers/page-directory/page-directory.ts